import { useState, useEffect, useMemo, useRef } from 'react';
import Icon from "../icons/Icon";
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useCompanyValidation } from '../../hooks/useCompanyValidation';
import { db } from '../../firebase/config';
import { collection, query, getDocs, serverTimestamp, doc, getDoc, runTransaction } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { fetchPayrollIndex, addIndexEntry, updateIndexEntry } from '../../services/payrollIndexService';
import { incrementPendingApproval } from '../../services/payrollApprovalSummaryService';
import { parsePeriodToDate, toYMDString } from '../../utils/periodDate';
import { DEFAULT_CUTOFF_START_DAYS } from '../../utils/payCutoffs';
import { calculateEmployeeFinancials, calculatePayrollSummary } from '../../utils/payrollCalculations';
import { loadRunEmployees, saveRunParent } from '../../services/payrollRunStore';
import {
  createAdjustment, updateAdjustment, voidAdjustment,
  fetchPendingInCycle, fetchConsumedByRun, fetchEmploymentByIds,
  rebuildRunAdjustments, mergePendingIntoRun,
} from '../../services/adjustmentService';
import {
  blocksForwardCommit, makeReentrancyGuard, buildDtrSnapshot, shouldRunInSessionStalenessCheck, employeesWithIncompleteRecords, isCountableDtrRecord, blocksWhileResumeRefreshing, reconcileResumeLines, blocksSubmitOnIncomplete,
  employeesWithSplitDays, splitDayReasonText, splitDayToastText,
  WIZARD_STEPS, WIZARD_STEP_KEYS, stepNumber, stepProgressWidth, nextStepKey, prevStepKey, resolveDraftStep,
  blocksAdvanceOnUnresolvedStaleness, disablesNextOnUnresolvedStaleness, disablesNextWhileChecking, preserveClientEdits,
} from './payrollGuards';
import PeriodSelection from './PeriodSelection';
import EmployeePayrollList from './EmployeePayrollList';
import GrossIncomeBreakdown from './GrossIncomeBreakdown';
import DeductionsStep from './DeductionsStep';
import LoansStep from './LoansStep';
import ManualAdjustments from './ManualAdjustments';
import PayrollSummary from './PayrollSummary';
import CompletePayrollModal from '../modals/CompletePayrollModal';
import { PHILIPPINE_LABOR_LAW_MINIMUMS } from '../../constants/laborLawMinimums';
import { useToast } from '../../contexts/ToastContext';
import '../../styles/sections/payroll.css';

// Default payroll rates from Philippine Labor Law minimums
const DEFAULT_PAYROLL_RATES = {
  ...PHILIPPINE_LABOR_LAW_MINIMUMS.rates,
  hoursPerDay: 8,
  // C1: annual paid-days divisor (monthly → daily); workingDaysPerMonth deleted (C5).
  dailyRateFactor: 365,
  // CBA premium base (PRD_CbaPremiumBase C19). REQUIRED here, not merely tidy:
  // `payrollRates` is built as {...DEFAULT_PAYROLL_RATES, ...ratesData}, and
  // ratesUsed below is a hand-listed write — so without these defaults a company
  // with no stored value would write `undefined` into Firestore and throw.
  // Deliberately NOT added to functions/src/payroll/constants.js: readers.js
  // spreads the stored doc SHALLOWLY over the defaults, so a default
  // `cbaPremiumBases` object would be replaced wholesale by a stored partial.
  // With no default declared there, `payrollRates.cbaPremiumBases || {}` yields
  // {} and the arithmetic is identical — the hazard is removed rather than
  // documented.
  minimumDailyWage: 0,
  cbaPremiumBases: { overtime: false, nightDiff: false, undertime: false },
};

// Default attendance policy values
const DEFAULT_ATTENDANCE_POLICY = {
  nightDiffStart: 22,
  nightDiffEnd: 6,
  leewayEarlyTimeIn: 30,
  leewayLateGracePeriod: 10,
  leewayOtStart: 15,
  leewayUndertimeGracePeriod: 10,
};

const ProcessPayroll = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { company, loading: companyLoading } = useCompanyValidation(slug);
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const draftRunId = searchParams.get('draft');
  const draftLoaded = useRef(false);
  const draftInitialSelectedIds = useRef(null);
  const persistedEmployeeIds = useRef(null); // employeeIds currently in the run's subcollection — the delete-diff base (impl-review #1)
  // F3(b): synchronous single-flight guard for recalc (a useRef so it blocks two
  // synchronous calls — a state flag wouldn't update in time; `isRecalculating`
  // state still drives the button label/disabled).
  const recalcGuard = useRef(makeReentrancyGuard()).current;
  // QW-1 (OBS-1): single-flight guard — coalesce concurrent saveDraft calls so two
  // overlapping saves can't pile mutations into the Firestore write queue (a suspected
  // cause of the resource-exhausted error). A concurrent caller awaits the in-flight save.
  const saveInFlight = useRef(null);
  // PRD_PayrollDraftLifecycle: a FRESH in-session run (set in handlePeriodChange; stays
  // false on resume — a resumed run is rebuilt from the live summary instead,
  // PRD_PayrollResumeReprice C2).
  const inSessionRunRef = useRef(false);
  // C4: in-memory DTR baseline captured at Step 3 ({ snapshot, periodStart, periodEnd }),
  // and a one-shot re-arm flag (set at each Step-3 re-baseline, cleared after the Summary check).
  const dtrBaselineRef = useRef(null);
  const summaryCheckArmed = useRef(false);
  // BL-018: live selection snapshot fed as initialSelectedIds on in-session Step-2 re-mount,
  // so a deselection survives back-nav (resume uses draftInitialSelectedIds instead).
  const inSessionSelectedIds = useRef(null);
  // CL-001 (C8): one-shot guard for the resume freshness refresh — stores the draftRunId it
  // ran for. Consumed only AFTER the hydration + settings gates pass, so a settings-first
  // render can't burn it on an un-hydrated draft and silently skip the refresh.
  const resumeRefreshDone = useRef(null);

  // C9 (PRD_LoansManagement): the draft's step is persisted by NAME; resolveDraftStep
  // (payrollGuards.js) still accepts the legacy six-step NUMBER a pre-deploy draft carries.

  // State management
  const [currentStep, setCurrentStep] = useState('period-selection');
  const [companySettings, setCompanySettings] = useState(null);
  const [payrollRates, setPayrollRates] = useState(DEFAULT_PAYROLL_RATES);
  const [attendancePolicy, setAttendancePolicy] = useState(DEFAULT_ATTENDANCE_POLICY);
  // The rates the SERVER applied on the last compute, straight off the callable.
  // Preferred over the live-settings state above wherever a rate is shown beside, or
  // persisted with, amounts the engine produced — the client's own copy is a one-shot
  // read at mount and goes stale the moment an admin edits Settings mid-session.
  // Null until the first compute returns (and when an older function build omits the
  // field), in which case everything falls back to live settings exactly as before.
  //
  // A RESUMED draft is rebuilt on resume (PRD_PayrollResumeReprice C2), so its Step-2→3
  // detail call sets this from the same compute that priced every line. The window this
  // closes on any run is a settings edit between mount and compute.
  const [computedRates, setComputedRates] = useState(null);
  const [payrollRun, setPayrollRun] = useState({
    companyId: '',
    runId: '',
    periodStart: null,
    periodEnd: null,
    payDate: null,
    frequency: 'weekly',
    employees: [],
    // Standalone adjustments the runner removed from THIS run (C19) — persisted
    // on the run parent (both parentData objects spread payrollRun) so the
    // resume rebuild (C14) doesn't resurrect them.
    excludedAdjustmentIds: [],
    payrollCycle: {
      startDate: null,
      endDate: null,
      frequency: 'weekly',
      cycleId: '',
      month: '',
      year: new Date().getFullYear()
    },
    summary: {
      totalEmployees: 0,
      totalGross: 0,
      totalDeductions: 0,
      totalNet: 0,
      byDepartment: {},
      deductionBreakdown: {
        sss: 0,
        philhealth: 0,
        pagibig: 0,
        tax: 0,
        other: 0
      }
    }
  });

  const [loading, setLoading] = useState(false);
  // C3: Step-2 load failure — server compute has no legacy fallback, so a failed
  // callable must surface a retryable error instead of an empty employee list.
  const [employeesError, setEmployeesError] = useState(null);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState(new Set());
  const [payrollIndexRuns, setPayrollIndexRuns] = useState([]);
  // C2: company pay-schedule cutoffs (settings/payroll doc; default = classic 1-15/16-end).
  const [cutoffStartDays, setCutoffStartDays] = useState(DEFAULT_CUTOFF_START_DAYS);
  // C9: forward-only floor for the Step-1 bridge chip — last completed run's end.
  const lastCompletedPeriodEnd = useMemo(() => {
    const ends = payrollIndexRuns
      .filter(r => r.status === 'completed' && r.periodEnd)
      .map(r => toYMDString(r.periodEnd)) // absorbs Timestamp | Date | 'YYYY-MM-DD' (never String().slice)
      .filter(Boolean)
      .sort();
    return ends.length ? ends[ends.length - 1] : null;
  }, [payrollIndexRuns]);
  const [isDraft, setIsDraft] = useState(false);
  const [hasEnteredStep2, setHasEnteredStep2] = useState(false); // BL-022: lock the period once Step 2 is entered (pre-save)
  const [draftResuming, setDraftResuming] = useState(!!draftRunId); // BL-022: hold a spinner until loadDraft sets the step (kill the Step-1 flash)
  const [draftLastSaved, setDraftLastSaved] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [staleEmployees, setStaleEmployees] = useState([]); // Employees with changed DTR since last save
  const [stalenessResolved, setStalenessResolved] = useState(false); // True after recalculation
  const [stalenessChecking, setStalenessChecking] = useState(false); // F3(a): background DTR-staleness check in flight
  const [isRecalculating, setIsRecalculating] = useState(false); // F3(b): recalc re-entrancy guard (dedicated, not `loading`)
  const [advancing, setAdvancing] = useState(false); // UX-1: Step-2→3 detail fetch in flight — lock Step-2 controls + label Next
  const [draftHydrated, setDraftHydrated] = useState(false); // CL-001 (C8): loadDraft hydrated a resumed draft — the reactive gate for the resume-refresh effect
  const [resumeRefreshing, setResumeRefreshing] = useState(false); // CL-001 (C10) / PRD_PayrollResumeReprice C2: set while the resume refresh runs, and left set if its summary call fails — holds Next/Submit until a reload
  const [resumeRebuildPending, setResumeRebuildPending] = useState(false); // PRD_PayrollResumeReprice C2: a resume rebuilt every line — show the blue "re-calculated on continue" banner at Step 2 until they advance
  const [resumeRefreshWarning, setResumeRefreshWarning] = useState(null); // PRD_PayrollResumeReprice C2/C6: amber banner text from the resume refresh — employees removed as no longer eligible, or the summary call failed (resumeRefreshing then stays set and the run is held until a reload)
  const [piiExcludedCount, setPiiExcludedCount] = useState(0); // Employees skipped because hasPii !== true (PRD §6.5)
  const [summaryTotals, setSummaryTotals] = useState({
    totalEmployees: 0,
    totalGross: 0,
    totalDeductions: 0,
    totalLoans: 0, // PRD_LoansManagement C8
    totalAdjustments: 0,
    totalNet: 0
  });
  // PRD_LoansManagement C13: the Loans step holds Next while its loans are loading or
  // the fetch errored (it sets this through onBlockedChange and clears it on unmount).
  const [loansStepBlocked, setLoansStepBlocked] = useState(false);

  // Memoize filtered payrollRun for PayrollSummary to prevent infinite re-renders
  // The inline array creation was causing new references on every render
  const payrollRunForSummary = useMemo(() => {
    if (currentStep !== 'summary') return null;

    const filteredEmployees = payrollRun.employees?.filter(emp =>
      selectedEmployeeIds.has(emp.employeeId || emp.id)
    ) || [];

    return {
      ...payrollRun,
      employees: filteredEmployees
    };
  }, [currentStep, payrollRun, selectedEmployeeIds]);

  // Check if any selected employees have unresolved OT (pending or rejected).
  // C6: the per-employee otPending/otRejected counts ride on every server summary
  // row, keyed on the canonical employeeId, so they read straight off the employee
  // lines — the old timeRecords aggregation is gone.
  const hasUnresolvedOT = useMemo(() => {
    if (selectedEmployeeIds.size === 0) return false;
    const employees = payrollRun.employees || [];
    const byId = new Map(employees.map(e => [e.employeeId || e.id, e]));
    for (const empId of selectedEmployeeIds) {
      const emp = byId.get(empId);
      if (emp && ((emp.otPending || 0) > 0 || (emp.otRejected || 0) > 0)) return true;
    }
    return false;
  }, [selectedEmployeeIds, payrollRun.employees]);

  // Selected employees whose net pay is below ₱0 (they owe more in deductions +
  // adjustments than they earned). A negative net must never be finalized — it would
  // push a negative amount into the CSV/bank disbursement file. Submit is hard-blocked
  // until the admin brings every net to ≥ ₱0. Uses the same calculateEmployeeFinancials
  // the Summary displays, so this matches the on-screen Net Pay column exactly.
  const negativeNetEmployees = useMemo(() => {
    return (payrollRun.employees || [])
      .filter(emp => selectedEmployeeIds.has(emp.employeeId || emp.id))
      .filter(emp => calculateEmployeeFinancials(emp).net < 0);
  }, [payrollRun.employees, selectedEmployeeIds]);
  const hasNegativeNet = negativeNetEmployees.length > 0;

  // Selected employees with an INCOMPLETE time record (one side missing) in the period —
  // PRD_DtrCompletenessGuard C3. Gate the wizard at employee-selection so an incomplete
  // record is caught up front (fail-fast) rather than only at Submit; the handleSubmitConfirm
  // backstop stays as defense-in-depth for anything created mid-run. Reads the
  // `hasIncompleteRecords` flag derived from the RAW records (server + legacy paths).
  const incompleteSelectedEmployees = useMemo(
    () => employeesWithIncompleteRecords(payrollRun.employees, selectedEmployeeIds),
    [payrollRun.employees, selectedEmployeeIds]
  );
  const hasIncompleteSelected = incompleteSelectedEmployees.length > 0;

  // Selected employees with more than one entry on a day (PRD_ShiftRules C4, BL-031): the
  // server keeps one record per date, so the run stops here instead of dropping the other.
  // Gated exactly where the incomplete block is.
  const splitDaySelectedEmployees = useMemo(
    () => employeesWithSplitDays(payrollRun.employees, selectedEmployeeIds),
    [payrollRun.employees, selectedEmployeeIds]
  );
  const hasSplitDaySelected = splitDaySelectedEmployees.length > 0;

  // Utility function to convert Firestore Timestamp to Date
  const toDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (value.toDate && typeof value.toDate === 'function') {
      // Firestore Timestamp
      return value.toDate();
    }
    if (typeof value === 'string' || typeof value === 'number') {
      return new Date(value);
    }
    return value;
  };

  // Fetch company settings and payroll rates
  useEffect(() => {
    const fetchCompanySettings = async () => {
      if (company) {
        try {
          // Fetch general settings
          const settingsDoc = await getDoc(doc(db, 'companies', company.id, 'settings', 'config'));
          const settings = settingsDoc.data();
          // CL-001: never store a nullish value — a missing settings/config doc yields
          // undefined, and the resume-refresh effect (holding the resume spinner) gates
          // on settings being loaded. `{}` reads identically through every `?.` consumer.
          setCompanySettings(settings || {});

          // Fetch payroll rates (financial rates only)
          const ratesDoc = await getDoc(doc(db, 'companies', company.id, 'settings', 'payrollRates'));
          if (ratesDoc.exists()) {
            const ratesData = ratesDoc.data();
            setPayrollRates({ ...DEFAULT_PAYROLL_RATES, ...ratesData });
          }

          // Fetch attendance policy (ND window, leeway)
          const policyDoc = await getDoc(doc(db, 'companies', company.id, 'settings', 'attendancePolicy'));
          if (policyDoc.exists()) {
            setAttendancePolicy({ ...DEFAULT_ATTENDANCE_POLICY, ...policyDoc.data() });
          }

          // Get the payroll frequency from settings
          const frequency = settings?.payroll?.frequency || 'weekly';

          // C2: pay-schedule cutoffs (settings/payroll subcollection doc).
          const payrollSettingsDoc = await getDoc(doc(db, 'companies', company.id, 'settings', 'payroll'));
          const storedCutoffs = payrollSettingsDoc.exists() ? payrollSettingsDoc.data().cutoffStartDays : undefined;
          if (Array.isArray(storedCutoffs) && storedCutoffs.length === 2) {
            setCutoffStartDays(storedCutoffs);
          }

          setPayrollRun(prev => ({
            ...prev,
            companyId: company.id,
            frequency: frequency
          }));

          // Fetch payroll index for conflict detection
          const indexRuns = await fetchPayrollIndex(company.id);
          setPayrollIndexRuns(indexRuns);
        } catch (error) {
          console.error('Error fetching company settings:', error);
          // CL-001: mark settings "loaded, empty" on failure — `null` means still in
          // flight, and other consumers gate on it; leaving it null would spin forever.
          setCompanySettings(prev => prev ?? {});
        }
      }
    };

    fetchCompanySettings();
  }, [company]);

  // Load draft from Firestore when ?draft= param is present
  useEffect(() => {
    const loadDraft = async () => {
      if (!draftRunId || !company || draftLoaded.current) return;

      // CL-001: a hydrated draft defers the spinner release to the resume-refresh effect
      // (which holds it until the freshness refresh resolves — no pre-clamp Summary flash).
      let deferSpinner = false;

      try {
        const draftDoc = await getDoc(doc(db, 'companies', company.id, 'payrollRuns', draftRunId));

        if (!draftDoc.exists()) {
          console.error('[loadDraft] Draft not found:', draftRunId);
          toast.error('Draft not found. It may have been deleted.');
          navigate(`/company/${slug}/payroll`);
          return;
        }

        const data = draftDoc.data();
        if (data.status !== 'draft') {
          console.error('[loadDraft] Document is not a draft:', data.status);
          toast.error('This payroll run is not a draft.');
          navigate(`/company/${slug}/payroll`);
          return;
        }


        // Convert period back to Date objects. Handles 'YYYY-MM-DD' strings
        // (fresh drafts) AND Firestore Timestamps (runs that went through Submit
        // then got rejected back to draft) — the naive string-concat parse
        // produced an Invalid Date for Timestamps and crashed PeriodSelection.
        const periodStart = parsePeriodToDate(data.periodStart);
        const periodEnd = parsePeriodToDate(data.periodEnd);

        // Hydrate employees via the storage helper: converted runs keep their
        // lines in the subcollection (the parent no longer carries `employees`,
        // C1/C3); historical inline runs return their parent array. Pass the
        // already-fetched parent `data` to avoid a redundant parent read.
        const employees = await loadRunEmployees(company.id, draftRunId, data);

        // Hydrate payrollRun state
        setPayrollRun(prev => ({
          ...prev,
          ...data,
          periodStart,
          periodEnd,
          employees,
          // Re-add summary structure (recomputed by the wizard from employees)
          summary: prev.summary,
        }));

        // Restore selected employee IDs
        if (data.selectedEmployeeIds && data.selectedEmployeeIds.length > 0) {
          setSelectedEmployeeIds(new Set(data.selectedEmployeeIds));
          draftInitialSelectedIds.current = data.selectedEmployeeIds;
        }

        // Restore current step
        let stepName = resolveDraftStep(data.currentStep); // C9: a name, or a legacy six-step number
        // A rejected run reverts to draft with lastRejection set. Resume at
        // Employee Selection — the period is already locked; the submitter is
        // fixing employees/data to resubmit. (Also sidesteps the Step-1
        // mount-before-load empty-field case: PeriodSelection isn't mounted on
        // resume and remounts populated when the user navigates back to Step 1.)
        if (data.lastRejection) stepName = 'employee-selection';
        setCurrentStep(stepName);

        // Mark as draft
        setIsDraft(true);
        setDraftLastSaved(data.lastModified?.toDate ? data.lastModified.toDate() : null);

        if (employees.length > 0) {
          // Prevent employee re-fetch from overwriting draft data
          draftLoaded.current = true;

          // Delete-diff base for the next save = the subcollection's ACTUAL contents
          // (the hydrated employees), not selectedEmployeeIds — so a stale/skewed
          // selection can't make the next reconcile under-delete (impl-review #1/#10).
          persistedEmployeeIds.current = new Set(employees.map(emp => emp.employeeId || emp.id));

          // CL-001 (C8): reactive hydration signal for the resume-refresh effect, which
          // runs in its own effect (this one never re-runs, so it cannot host it).
          setDraftHydrated(true);
          deferSpinner = true;
        } else {
          // C5: a saved draft whose subcollection is EMPTY (cannot happen under C1, but
          // defend in depth). Do NOT set draftLoaded.current — that would permanently
          // suppress the eligible-employee re-fetch and dead-end Step 2 at zero employees.
          // Re-fetch the eligible list so the user can reselect (mirror the R2 re-gating —
          // off the missing data, not the blanket flag).
          persistedEmployeeIds.current = new Set();
          fetchEmployeesForPayroll(periodStart, periodEnd, company.id);
        }
      } catch (err) {
        console.error('[loadDraft] Error loading draft:', err);
        toast.error('Failed to load draft. Please try again.');
      } finally {
        // BL-022(1): clear the resume spinner once the step is set (kill the Step-1 flash).
        // CL-001: unless a hydrated draft deferred the release to the resume-refresh effect.
        if (!deferSpinner) setDraftResuming(false);
      }
    };

    loadDraft();
  }, [company, draftRunId]);

  // PRD_PayrollResumeReprice C2/C6 (on CL-001 C8/C10): resume-time refresh. A saved draft
  // holds lines priced when it was last saved, so on EVERY resume each line is rebuilt the
  // way a fresh run builds it: the eligibility summary is re-read (read-only callable) and
  // reconcileResumeLines turns every present, eligible line into the live Step-2 summary row
  // with the runner's edits re-applied — current OT counts, incomplete flag, day counts and
  // rate at Step 2, and no `timeRecords`, so the Step-2→3 detail refetch re-prices every
  // line at current settings. Lines a fresh run would exclude (absent from the summary, or
  // `isEligible === false`) are removed from the run and named in the amber warning. The
  // step is clamped back to Employee Selection so the walk always passes through the rebuild.
  //
  // If the summary call fails, `resumeRefreshing` stays set: Next and Submit stay held
  // (removal needs the summary rows, and the detail call does not exclude ineligible
  // employees), and a reload retries.
  //
  // Gate: hydration (draftHydrated) is checked BEFORE the one-shot is consumed — burning
  // the one-shot on an un-hydrated render would silently skip the refresh.
  useEffect(() => {
    if (!draftRunId || !draftHydrated) return;
    if (resumeRefreshDone.current === draftRunId) return; // one-shot per draft
    resumeRefreshDone.current = draftRunId;

    const refresh = async () => {
      setResumeRefreshing(true);
      try {
        const data = await callEligibilitySummary(payrollRun.periodStart, payrollRun.periodEnd);
        const liveRows = data.employees || [];

        // Membership and eligibility don't depend on adjustments, so the closure decides
        // who is removed; the lines themselves are rebuilt from `prev` below.
        const { removed } = reconcileResumeLines(payrollRun.employees, liveRows);
        const removedIds = new Set(removed.map(r => r.employeeId));
        if (removed.length > 0) {
          setResumeRefreshWarning(`Removed from this run — no longer eligible: ${removed.map(r => `${r.employeeId} (${r.reason})`).join(', ')}`);
        }

        // Drop removed ids from the LIVE selection and from inSessionSelectedIds, which
        // EmployeePayrollList reads as initialSelectedIds when it mounts after the resume
        // spinner. Functional read: after the await above, the closure's
        // selectedEmployeeIds may be stale — `prev` is not.
        setSelectedEmployeeIds(prev => {
          const next = new Set([...prev].filter(id => !removedIds.has(id)));
          inSessionSelectedIds.current = Array.from(next);
          return next;
        });
        // Rebuild from `prev`, not the closure: the C14 adjustment refresh fires on the same
        // draftHydrated commit and usually lands first, and a closure-based write would
        // revert it.
        setPayrollRun(prev => ({
          ...prev,
          employees: reconcileResumeLines(prev.employees, liveRows).employees,
        }));
        // Clamp: the rebuild only runs on the Step-2→3 transition, so route the walk
        // through it no matter where the draft was saved (incl. a Summary resume).
        setCurrentStep(prev =>
          (prev === 'period-selection' || prev === 'employee-selection') ? prev : 'employee-selection'
        );
        // Persistent on-page blue banner (not a transient toast) so a resume that bounced
        // the user back to Step 2 explains itself. Cleared when they advance past Step 2.
        setResumeRebuildPending(true);
        setResumeRefreshing(false);
      } catch (err) {
        // Hold: leave resumeRefreshing set, so Next and Submit stay blocked until a reload.
        console.error('[resumeRefresh] Eligibility refresh failed — holding the run:', err);
        setResumeRefreshWarning('Couldn\'t re-check this draft against current rates and time records. Refresh the page to continue.');
      } finally {
        setDraftResuming(false); // release the held resume spinner
      }
    };
    refresh();
  }, [draftRunId, draftHydrated]);

  // Background staleness check — compares a DTR baseline snapshot (the in-memory one captured
  // at Step 2→3 of an in-session run) against the current DTR data
  const checkDtrStaleness = async (snapshot, periodStart, periodEnd, employees) => {
    if (!company) return;

    // F3(a): mark the check in flight so forward-commit affordances (Next / Submit /
    // Recalculate) block-and-return until it resolves — else they fire on stale gross
    // income during the few-second check window. Cleared in finally so a thrown or
    // empty check always clears it (no deadlock, no timeout).
    setStalenessChecking(true);
    try {
      const { getMonthsInRange } = await import('../../utils/periodDate');
      const affectedMonths = getMonthsInRange(periodStart, periodEnd);
      // Recount each employee's current DTR in PARALLEL (one getDoc per employee per month).
      const results = await Promise.all(employees.map(async (emp) => {
        const empId = emp.employeeId || emp.id;
        const savedSnapshot = snapshot[empId];
        if (!savedSnapshot) return null;

        // Recount over the window the baseline actually covers — derived from the run's
        // own records (emp.timeRecords), not re-clamped from endDate — so a partial-period
        // employee isn't falsely flagged stale.
        // (Falls back to the full period when the run carries no records.)
        // A record added OUTSIDE this span isn't flagged — but for a partial-period
        // employee that's outside their employment window and isn't payable anyway.
        const savedDates = (emp.timeRecords || [])
          .filter(r => r.date && !r.isPlaceholder)
          .map(r => r.date);
        const effStart = savedDates.length ? savedDates.reduce((a, b) => (a < b ? a : b)) : periodStart;
        const effEnd = savedDates.length ? savedDates.reduce((a, b) => (a > b ? a : b)) : periodEnd;

        // Fetch this employee's DTR docs (all months) in parallel.
        const docs = await Promise.all(affectedMonths.map((month) =>
          getDoc(doc(db, 'companies', company.id, 'dailyTimeRecords', `${month}_${empId}`))));

        let currentRecordCount = 0;
        let currentTotalHours = 0;
        let currentTotalOT = 0;
        docs.forEach((dtrDoc) => {
          if (!dtrDoc.exists()) return;
          // Match the snapshot's record set EXACTLY — same countable-record predicate the
          // baseline (buildDtrSnapshot) uses: excludes synthesized placeholders AND incomplete
          // one-sided orphans (which are stripped from emp.timeRecords, so the raw recount must
          // exclude them too, else a phantom "records modified" banner — PRD_DtrCompletenessGuard).
          const records = (dtrDoc.data().timeRecords || []).filter(r =>
            r.date && r.date >= effStart && r.date <= effEnd && isCountableDtrRecord(r)
          );
          currentRecordCount += records.length;
          currentTotalHours += records.reduce((sum, r) => sum + (r.hoursWorked || 0), 0);
          currentTotalOT += records.reduce((sum, r) => sum + (r.overtimeHours || 0), 0);
        });

        // Compare against saved snapshot
        const changes = [];
        if (currentRecordCount !== savedSnapshot.recordCount) {
          const diff = currentRecordCount - savedSnapshot.recordCount;
          changes.push(diff > 0 ? `${diff} new record(s) added` : `${Math.abs(diff)} record(s) removed`);
        }
        if (Math.abs(currentTotalHours - savedSnapshot.totalHoursWorked) > 0.01) {
          changes.push(`hours changed: ${savedSnapshot.totalHoursWorked.toFixed(1)}h → ${currentTotalHours.toFixed(1)}h`);
        }
        if (Math.abs(currentTotalOT - savedSnapshot.totalOvertimeHours) > 0.01) {
          changes.push(`OT changed: ${savedSnapshot.totalOvertimeHours.toFixed(1)}h → ${currentTotalOT.toFixed(1)}h`);
        }

        if (changes.length === 0) return null;
        return {
          employeeId: empId,
          employeeName: emp.personalInfo?.fullName || emp.employeeName || empId,
          changes,
        };
      }));

      const stale = results.filter(Boolean);
      if (stale.length > 0) {
        setStaleEmployees(stale);
      }
    } catch (err) {
      console.error('[checkDtrStaleness] Error during staleness check:', err);
      // Non-blocking — if check fails, proceed without warning
    } finally {
      setStalenessChecking(false);
    }
  };

  // Re-fetch employees when we're on basic-pay step and have period data
  // NOTE (PRD_LoansManagement C1): 'basic-pay' was a wizard step until the
  // PayrollProcessUI rework removed it (see 8b158b0); it is not a WIZARD_STEPS key,
  // so this effect never fires. resolveDraftStep maps a draft saved on it to
  // 'period-selection'. Surfaced by the STEPS refactor; left in place.
  useEffect(() => {
    // Only fetch if we don't already have employee data
    if (currentStep === 'basic-pay' && payrollRun.periodStart && payrollRun.periodEnd && company) {
      if (!payrollRun.employees || payrollRun.employees.length === 0) {
        fetchEmployeesForPayroll(payrollRun.periodStart, payrollRun.periodEnd, company.id);
      } else {
      }
    }
  }, [currentStep]); // Only re-run when step changes

  // Scroll back to the top on every step change (Next / Back / stepper click /
  // draft restore) so each step starts from the page top, not mid-scroll.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  // C4: on landing on Summary for a FRESH in-session run, re-read DTR vs the
  // in-memory baseline captured at Step 3 and flag staleness (disable Submit + show the
  // banner + Recalculate — all existing machinery). Fires once per arming (re-armed at each
  // Step-3 re-baseline). A resumed run is gated out here — it is rebuilt from the live
  // summary on resume instead (PRD_PayrollResumeReprice C2).
  useEffect(() => {
    if (!shouldRunInSessionStalenessCheck({
      currentStep,
      inSession: inSessionRunRef.current,
      armed: summaryCheckArmed.current,
      hasBaseline: !!dtrBaselineRef.current,
    })) return;
    summaryCheckArmed.current = false; // fire once per arming (re-armed on the next Step-3 re-baseline)
    const { snapshot, periodStart, periodEnd } = dtrBaselineRef.current;
    const selected = (payrollRun.employees || []).filter(emp => selectedEmployeeIds.has(emp.employeeId || emp.id));
    checkDtrStaleness(snapshot, periodStart, periodEnd, selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  // Generate unique cycle ID based on dates and frequency
  const generateCycleId = (startDate, endDate, frequency) => {
    if (!startDate || !endDate) return '';
    const start = new Date(startDate).toISOString().split('T')[0];
    const end = new Date(endDate).toISOString().split('T')[0];
    return `${frequency}_${start}_${end}`;
  };

  // Claim a runId by atomically writing the parent, at the FIRST Save & Exit / Submit
  // (PRD_PayrollDraftLifecycle C2 — amends PayrollSavePerf C4 reserve-at-creation). Nothing
  // is written until the user saves; this reservation:
  //   - uses a `runTransaction` WRITE-IF-ABSENT (the Web SDK has no `create()`), so two
  //     tabs computing the same scanned runId can't clobber each other — the loser retries
  //     with the next id;
  //   - returns the claimed id (the caller threads this LOCAL through the rest of the save —
  //     `payrollRun.runId` is still falsy on the same tick, React hasn't committed setPayrollRun).
  // The transient draft parent it writes is overwritten by the caller's saveRunParent
  // (merge) on the same call; the employee subcollection is written right after (C1).
  const reserveRunId = async (period) => {
    for (let attempt = 0; attempt < 5; attempt++) {
      const runId = await generatePayrollRunId(period.startDate, company.id);
      const ref = doc(db, 'companies', company.id, 'payrollRuns', runId);
      try {
        await runTransaction(db, async (tx) => {
          const snap = await tx.get(ref);
          if (snap.exists()) {
            const collision = new Error('runId already exists');
            collision.code = 'run-exists';
            throw collision;
          }
          tx.set(ref, {
            status: 'draft',
            storageShape: 'subcollection',
            periodStart: toYMDString(period.startDate),
            periodEnd: toYMDString(period.endDate),
            calendarDays: period.calendarDays,
            selectedEmployeeIds: [],
            currentStep: 'employee-selection', // C9: by NAME, like doSaveDraft
            createdAt: serverTimestamp(),
            createdBy: user.userData?.userDocId || user.uid,
            lastModified: serverTimestamp(),
            lastModifiedBy: user.userData?.userDocId || user.uid,
          });
        });
        return runId;
      } catch (err) {
        if (err.code === 'run-exists') continue; // collision — regenerate the next id
        throw err; // a real write/permission error
      }
    }
    throw new Error('Could not reserve a payroll run id (too many collisions).');
  };

  // Period selection handler
  const handlePeriodChange = async (period) => {
    // When resuming a draft, keep the existing runId and skip employee re-fetch
    if (isDraft) {
      console.log('[handlePeriodChange] Draft mode — skipping runId generation, advancing to Step 2');
      setCurrentStep('employee-selection');
      return;
    }

    // C1: write NOTHING on Step 1→2 — the run lives in client state until the first
    // Save & Exit / Submit, which claims the runId (reserveRunId). No parent doc, no
    // index entry, no soft-lock until then.
    const updatedPayrollRun = {
      ...payrollRun,
      runId: null, // claimed at first save (C2)
      periodStart: period.startDate,
      periodEnd: period.endDate,
      calendarDays: period.calendarDays
    };

    setPayrollRun(updatedPayrollRun);
    setIsDraft(false); // nothing persisted yet — the first save takes the create branch
    inSessionRunRef.current = true; // a fresh in-session run (drives the Summary staleness check; C4)
    setHasEnteredStep2(true); // lock the period field from here on (BL-022)
    persistedEmployeeIds.current = new Set(); // empty subcollection — delete-diff base

    setCurrentStep('employee-selection');

    // Pass period data directly to avoid closure issues
    fetchEmployeesForPayroll(period.startDate, period.endDate, company?.id);
  };

  // Generate unique payroll run ID with dynamic sequencing
  const generatePayrollRunId = async (periodStart, companyId) => {
    const date = new Date(periodStart);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const yearMonth = `${year}${month}`;

    try {
      // Query all runs and filter client-side to find matching yearMonth
      // This avoids requiring a special index on document IDs
      const q = query(
        collection(db, 'companies', companyId, 'payrollRuns')
      );

      const snapshot = await getDocs(q);
      let sequence = 1;

      // Filter for documents matching this yearMonth and find highest sequence
      const matchingDocs = snapshot.docs
        .filter(doc => doc.id.startsWith(`${yearMonth}-`))
        .map(doc => {
          const parts = doc.id.split('-');
          return parts.length === 2 ? parseInt(parts[1], 10) : 0;
        })
        .filter(num => !isNaN(num));

      if (matchingDocs.length > 0) {
        sequence = Math.max(...matchingDocs) + 1;
      }

      const sequenceStr = String(sequence).padStart(3, '0');
      const runId = `${yearMonth}-${sequenceStr}`;
      
      return runId;
    } catch (error) {
      console.error('[generatePayrollRunId] Error generating runId:', error);
      // Fallback to 001 if query fails
      return `${yearMonth}-001`;
    }
  };


  // F2/C1/C3: load the Step-2 eligible-employee SUMMARY from the server callable
  // (computePayrollEligibility, view 'summary'). One bulk server compute replaces
  // the client's serial per-employee DTR fan-out; the compact summary carries
  // per-employee counts/OT/eligibility/earnings-totals (raw timeRecords + full
  // detail are fetched on demand at Step 3, C4). Throws on failure; the caller
  // (fetchEmployeesForPayroll) turns that into the C3 retryable error state.
  // CL-001: data-RETURNING summary read — the callable invocation writes NO run state,
  // so the resume refresh can reconcile a hydrated draft against the live rows without
  // touching it. (It does set `computedRates`, which is display/audit metadata only and
  // never touches `payrollRun` — the thing CL-001 protects.) (fetchEligibilitySummary below REPLACES payrollRun.employees; calling
  // THAT on a resumed draft would wipe the saved earnings, deductions, adjustments
  // and timeRecords.)
  const callEligibilitySummary = async (periodStart, periodEnd) => {
    const functions = getFunctions(undefined, 'asia-southeast1');
    const computeEligibility = httpsCallable(functions, 'computePayrollEligibility');
    const { data } = await computeEligibility({
      periodStart: toYMDString(periodStart),
      periodEnd: toYMDString(periodEnd),
      view: 'summary',
    });
    if (data.ratesApplied) setComputedRates(data.ratesApplied);
    return data;
  };

  const fetchEligibilitySummary = async (periodStart, periodEnd) => {
    const data = await callEligibilitySummary(periodStart, periodEnd);
    const summaryEmployees = data.employees || [];
    setPiiExcludedCount(data.piiExcludedCount || 0);
    setPayrollRun(prev => ({
      ...prev,
      employees: summaryEmployees,
      summary: {
        ...prev.summary,
        totalEmployees: summaryEmployees.length,
        totalGross: data.summary?.totalGross || 0,
      },
    }));
  };

  // C4: fetch the full per-employee DETAIL (raw timeRecords + earnings detail +
  // deductions + monthlyDtrData + PII) for the selected set, via the callable's
  // detail view. Returns the detail employees array; callers merge it into state.
  const fetchDetailView = async (employeeIds) => {
    const functions = getFunctions(undefined, 'asia-southeast1');
    const computeEligibility = httpsCallable(functions, 'computePayrollEligibility');
    const { data } = await computeEligibility({
      periodStart: toYMDString(payrollRun.periodStart),
      periodEnd: toYMDString(payrollRun.periodEnd),
      view: 'detail',
      employeeIds,
    });
    if (data.ratesApplied) setComputedRates(data.ratesApplied);
    return data.employees || [];
  };

  // PRD_PayrollSavePerf C1/C2: server-side child write. The client sends its already-computed
  // reviewed employees to the savePayrollRun callable, which BulkWriter-upserts them (+ deletes
  // the deselected diff) server-side — the ~698-doc write that saturated the client Web SDK
  // write stream (OBS-1). The client writes the parent AFTER this returns (children-first/parent-last).
  const callSavePayrollRun = async ({ runId, employees, previousEmployeeIds, mode }) => {
    const functions = getFunctions(undefined, 'asia-southeast1');
    const fn = httpsCallable(functions, 'savePayrollRun');
    await fn({ runId, employees, previousEmployeeIds, mode });
  };

  // Fetch employees for payroll with time records aggregation
  const fetchEmployeesForPayroll = async (periodStart = null, periodEnd = null, companyId = null) => {
    // Skip if draft was loaded — employees are already in state from the draft document
    if (draftLoaded.current) {
      console.log('[fetchEmployeesForPayroll] Skipped — draft already loaded with employees');
      return;
    }

    // Use passed parameters or fall back to state - ensure they are Date objects
    const actualPeriodStart = toDate(periodStart || payrollRun.periodStart);
    const actualPeriodEnd = toDate(periodEnd || payrollRun.periodEnd);
    const actualCompanyId = companyId || company?.id;

    // Check if we have the required period data
    if (!actualPeriodStart || !actualPeriodEnd) {
      return;
    }

    if (!actualCompanyId) {
      return;
    }

    // Show the top-level loading spinner for the whole fetch (incl. the draft getDoc await
    // below), so Step 2 never briefly renders its "No eligible employees" empty state while
    // employees are still loading — UI-006 follow-up. Set after the guards so an invalid
    // period/company early-return doesn't leave loading stuck on.
    setLoading(true);

    // Server compute is the single, unconditional path (C1). A draft resume bypasses
    // both callables (C5).
    //
    // C3: there is no legacy fallback to degrade to, so a failure surfaces as a
    // retryable Step-2 error. This function NEVER re-throws — it is called
    // fire-and-forget (no await, no .catch) from loadDraft, the step-change effect and
    // handlePeriodChange, so throwing would become an unhandled promise rejection.
    setEmployeesError(null);
    try {
      await fetchEligibilitySummary(actualPeriodStart, actualPeriodEnd);
    } catch (err) {
      console.error('[fetchEmployeesForPayroll] Server compute failed', err);
      // Void the state this compute was supposed to produce. Three Step-2 surfaces derive
      // from it and every one of them read as PARTIAL SUCCESS above the error card when it
      // survived a failure that followed a success (found at /test-impl, then /impl-review):
      // the PII banner (:2137, `piiExcludedCount` is written ONLY on the success path at
      // :797 and had no reset), the incomplete-records banner (:2196, which told the user to
      // "de-select" rows in a table the error branch had already hidden), and
      // EmployeePayrollList's excluded panel. Clearing at the source fixes all three — and
      // the retry window too, where `setEmployeesError(null)` above re-exposes them under
      // the spinner. Safe: a draft resume returns at :836 before reaching this catch, so
      // hydrated draft rows are never wiped, and `[]` keeps every `.find`/`.filter` consumer
      // working.
      setPiiExcludedCount(0);
      setPayrollRun(prev => ({ ...prev, employees: [] }));
      setEmployeesError(
        "Couldn't load employees for this period. Check your connection and try again."
      );
      toast.error("Couldn't load employees for this period.");
    } finally {
      setLoading(false);
    }
  };

  // Process payroll - changes status to completed
  const handleProcessPayroll = async () => {
    // F3(a): landing on Summary runs the in-session background staleness check — block
    // Submit while it's in flight (the Submit button's staleEmployees disable is
    // vacuous until the check resolves), else the commit fires on stale gross income.
    if (blocksForwardCommit(stalenessChecking, 'submit')) {
      toast.warning('Checking time records… please wait a moment and try again.');
      return;
    }
    // CL-001 (C10): never open the confirm modal while the resume refresh is in flight.
    if (blocksWhileResumeRefreshing(resumeRefreshing, 'submit')) {
      toast.warning('Refreshing the saved draft… please wait a moment and try again.');
      return;
    }
    // CL-001: C3 incomplete-records gate at the modal-open point too — a Summary resume
    // whose refresh just set the flag has no Step-2 banner on screen, so the disabled
    // button + this guard are what stop the commit (handleSubmitConfirm backstops it).
    if (blocksSubmitOnIncomplete(payrollRun.employees, selectedEmployeeIds)) {
      toast.error('Can\'t submit — a selected employee has an incomplete time record (missing a time-in or time-out). Fix it in Employee Time Records (filter: Incomplete), or de-select the employee.');
      return;
    }
    // Defense in depth: Next is already disabled on Deductions/Adjustments while any net
    // is below ₱0, so a negative-net run can't reach this point. Keep the guard (no toast
    // needed) and the disabled Submit button as a backstop against a negative disbursement.
    if (hasNegativeNet) return;
    // Show the complete modal instead of processing directly
    setShowCompleteModal(true);
  };

  // Submit payroll for approval (PRD_PayrollApproval.md §Side Effect Migration).
  // This is Phase-1 of the two-phase Submit/Approve flow: status → pending-approval,
  // DTRs hard-locked, summary counter incremented. NO deduction write-back here —
  // that runs on Approve in payrollCompletionService.approvePayroll.
  const handleSubmitConfirm = async () => {
    setShowCompleteModal(false);

    // F3(a) backstop on the side-effecting commit: never submit while the background
    // staleness check is still running (mirrors the hasNegativeNet backstop below) —
    // Submit flips status→pending-approval and hard-locks DTRs.
    if (blocksForwardCommit(stalenessChecking, 'submit')) {
      toast.warning('Checking time records… please wait a moment and try again.');
      return;
    }

    // CL-001 (C10) backstop: never commit while the resume freshness refresh is in flight.
    if (blocksWhileResumeRefreshing(resumeRefreshing, 'submit')) {
      toast.warning('Refreshing the saved draft… please wait a moment and try again.');
      return;
    }

    // True backstop: never finalize a run with a negative net pay (the disabled Submit
    // button + handleProcessPayroll guard make this unreachable in normal flow, but this
    // is the actual side-effecting entry point).
    if (hasNegativeNet) return;

    // Backstop (PRD_DtrCompletenessGuard C3): never finalize while any SELECTED employee has an
    // incomplete time record — a record missing exactly one of time-in/time-out can't be computed.
    // Placeholders (synthesized unpunched days) are excluded. Resolve by completing the pair or
    // deleting the record (Employee Time Records → filter Incomplete), or de-select the employee.
    const incompleteEmployees = employeesWithIncompleteRecords(payrollRun.employees, selectedEmployeeIds);
    if (incompleteEmployees.length > 0) {
      const names = incompleteEmployees.map((e) => e.employeeId || e.id).join(', ');
      toast.error(
        `Can't submit — incomplete time records (missing a time-in or time-out) for: ${names}. ` +
        'Fix them in Employee Time Records (filter: Incomplete), or de-select the employee to pay them next cycle.'
      );
      return;
    }
    // Backstop (PRD_ShiftRules C4): never finalize while a selected employee has more than one
    // entry on a day — the day build would pay one of them and drop the other.
    const splitDayEmployees = employeesWithSplitDays(payrollRun.employees, selectedEmployeeIds);
    if (splitDayEmployees.length > 0) {
      toast.error(splitDayToastText(splitDayEmployees));
      return;
    }

    try {
      setLoading(true);

      const cycleId = generateCycleId(
        payrollRun.periodStart,
        payrollRun.periodEnd,
        payrollRun.frequency
      );

      // Filter employees by selection and strip stale 'totals' (recomputed on-demand)
      const filteredEmployees = (payrollRun.employees?.filter(emp =>
        selectedEmployeeIds.has(emp.employeeId || emp.id)
      ) || []).map(emp => {
        const { totals, ...employeeWithoutTotals } = emp;
        return employeeWithoutTotals;
      });

      const userId = user.userData?.userDocId || user.uid;
      const nowJs = new Date();
      const isResubmission = !!payrollRun.lastRejection;
      const existingTrail = Array.isArray(payrollRun.auditTrail) ? payrollRun.auditTrail : [];
      const newTrailEntry = {
        action: isResubmission ? 'resubmitted' : 'submitted',
        by: userId,
        at: nowJs,
        reason: null,
      };

      const { summary: _unusedSummary, ...payrollRunWithoutSummary } = payrollRun;
      const submitData = {
        ...payrollRunWithoutSummary,
        employees: filteredEmployees,
        // selectedEmployeeIds must survive the submit overwrite — the reject
        // cleanup derives employeeIds from it (C6); without it a submitted-then-
        // rejected run strands its pending-approval DTR locks.
        selectedEmployeeIds: Array.from(selectedEmployeeIds),
        // Denormalized aggregates on the parent (C2), matching saveDraft.
        summary: calculatePayrollSummary(filteredEmployees),
        // Persist period boundaries as 'YYYY-MM-DD' strings, not the Date the
        // spread would serialize to a Timestamp (the class that broke locks +
        // resume). saveDraft already does this; submit must too.
        periodStart: toYMDString(payrollRun.periodStart),
        periodEnd: toYMDString(payrollRun.periodEnd),
        // The audit snapshot records what the run was COMPUTED at, so it must come from
        // the server's own read, not this client's mount-time copy — those diverge the
        // moment an admin edits a rate mid-session, and the payslip reads this field
        // forever. `computedRates` carries every key below; the client-sourced object is
        // the fallback for an older function build that does not return them.
        ratesUsed: computedRates || {
          overtimeRate: payrollRates.overtimeRate,
          nightDiffRate: payrollRates.nightDiffRate,
          regularHolidayRate: payrollRates.regularHolidayRate,
          specialHolidayRate: payrollRates.specialHolidayRate,
          restDayRate: payrollRates.restDayRate,
          restDayOvertimeRate: payrollRates.restDayOvertimeRate,
          hoursPerDay: attendancePolicy.hoursPerDay || payrollRates.hoursPerDay || 8, // mirror the engine's resolution (|| so a stored 0 → 8)
          dailyRateFactor: payrollRates.dailyRateFactor,
          // PRD_CbaPremiumBase C19 — the two keys the pay figures were computed
          // FROM. Scoping this to the arithmetic (and excluding the Region and
          // Sector that once resolved a floor) is what let Amendment A withdraw
          // that floor without touching the audit snapshot at all.
          // Forward-only: existing runs are not backfilled.
          minimumDailyWage: payrollRates.minimumDailyWage,
          cbaPremiumBases: payrollRates.cbaPremiumBases,
        },
        policyUsed: {
          nightDiffStart: attendancePolicy.nightDiffStart,
          nightDiffEnd: attendancePolicy.nightDiffEnd,
          leewayEarlyTimeIn: attendancePolicy.leewayEarlyTimeIn,
          leewayLateGracePeriod: attendancePolicy.leewayLateGracePeriod,
          leewayOtStart: attendancePolicy.leewayOtStart,
          leewayUndertimeGracePeriod: attendancePolicy.leewayUndertimeGracePeriod,
        },
        status: 'pending-approval',
        submittedBy: userId,
        submittedAt: nowJs,
        lastRejection: null,
        auditTrail: [...existingTrail, newTrailEntry],
        approvalSnapshot: null,
        lastModified: serverTimestamp(),
        lastModifiedBy: userId,
        payrollCycle: {
          startDate: toYMDString(payrollRun.periodStart),
          endDate: toYMDString(payrollRun.periodEnd),
          frequency: payrollRun.frequency,
          cycleId: cycleId,
          // Local month, not toISOString (UTC would shift a period-start-on-the-1st)
          month: toYMDString(payrollRun.periodStart).slice(0, 7),
          year: parsePeriodToDate(payrollRun.periodStart)?.getFullYear() || new Date().getFullYear()
        },
        createdAt: payrollRun.createdAt || serverTimestamp(),
      };

      // C2: a run built straight to Submit with no prior Save & Exit has no runId yet —
      // claim it now (write-if-absent + collision-retry) and thread the returned LOCAL
      // `runId` through every write below (`payrollRun.runId` is still falsy on this tick).
      let runId = payrollRun.runId;
      if (!runId) {
        runId = await reserveRunId({
          startDate: payrollRun.periodStart,
          endDate: payrollRun.periodEnd,
          calendarDays: payrollRun.calendarDays,
        });
      }
      submitData.runId = runId;

      // Persist (C1/C2): children SERVER-SIDE via the savePayrollRun callable (BulkWriter),
      // then the client writes the pending-approval parent LAST (children-first/parent-last).
      // The status flip / index / counter / audit stay client-side (PRD_PayrollApproval).
      await callSavePayrollRun({
        runId,
        employees: filteredEmployees,
        previousEmployeeIds: Array.from(persistedEmployeeIds.current || []),
        mode: 'submit',
      });
      await saveRunParent(company.id, runId, submitData);
      persistedEmployeeIds.current = new Set(filteredEmployees.map(emp => emp.employeeId || emp.id));
      setPayrollRun(submitData);
      toast.success('Payroll submitted for approval!');

      // Update payroll index
      try {
        const employeeIds = filteredEmployees.map(emp => emp.employeeId || emp.id);
        const pStart = toYMDString(payrollRun.periodStart);
        const pEnd = toYMDString(payrollRun.periodEnd);
        const indexEntry = {
          runId,
          periodStart: pStart,
          periodEnd: pEnd,
          status: 'pending-approval',
          employeeIds,
        };
        if (isDraft) {
          await updateIndexEntry(company.id, runId, indexEntry);
        } else {
          await addIndexEntry(company.id, indexEntry);
        }
      } catch (indexErr) {
        console.error('[handleSubmitConfirm] Failed to update payroll index:', indexErr);
        // The index entry IS the hard lock now (C1): a failed write can leave this
        // run's records editable until the next save/reconcile — surface it rather
        // than swallow (C9 mitigation i / impl-review F3).
        toast.warning('Payroll submitted, but updating the lock index failed — some records may stay editable. Retry, or run the payroll-index reconcile.');
      }

      // DTR locks are derived from the payroll index (C1): the submit above wrote
      // the index entry as 'pending-approval', so the hard lock applies with no
      // per-employee fan-out.

      // Increment pending-approval counter for dashboard badge + approval page
      try {
        await incrementPendingApproval(company.id, 1);
      } catch (summaryErr) {
        console.error('[handleSubmitConfirm] Failed to increment pending counter (non-blocking):', summaryErr);
      }

      setTimeout(() => {
        navigate(`/company/${slug}/payroll`);
      }, 1500);
    } catch (err) {
      console.error('Error submitting payroll:', err);

      const message = err.message || '';
      let userMessage = 'Failed to submit payroll. Please try again.';

      if (message.includes('permissions') || message.includes('permission')) {
        userMessage = 'Failed to submit payroll. You do not have permission to perform this action.';
      } else if (message.includes('offline') || message.includes('DEADLINE_EXCEEDED') || message.includes('unavailable')) {
        userMessage = 'Failed to submit payroll. Please check your internet connection and try again.';
      } else if (message.includes('Quota exceeded')) {
        userMessage = 'Failed to submit payroll. Service limit reached. Please try again later.';
      } else if (message.includes('undefined') || message.includes('null')) {
        userMessage = 'Failed to submit payroll. Some required data is missing. Please go back and verify all steps are complete.';
      }

      toast.error(userMessage);
    } finally {
      setLoading(false);
    }
  };

  // Update employee data
  const handleEmployeeUpdate = (employeeId, updates) => {
    setPayrollRun(prev => {
      const updatedEmployees = prev.employees.map(emp => {
        if (emp.employeeId === employeeId) {
          // Create a copy of the employee
          const updatedEmp = { ...emp };

          // Deep merge for earnings if present in updates
          if (updates.earnings) {
            updatedEmp.earnings = {
              ...emp.earnings,
              ...updates.earnings
            };
          }

          // Deep merge for deductions if present in updates
          if (updates.deductions) {
            updatedEmp.deductions = {
              ...emp.deductions,
              ...updates.deductions
            };
          }

          // Update timeRecords if provided
          if (updates.timeRecords) {
            updatedEmp.timeRecords = updates.timeRecords;
          }

          // Copy any other top-level updates (excluding earnings, deductions, and timeRecords)
          Object.keys(updates).forEach(key => {
            if (!['earnings', 'deductions', 'timeRecords'].includes(key)) {
              updatedEmp[key] = updates[key];
            }
          });

          return updatedEmp;
        }
        return emp;
      });

      return {
        ...prev,
        employees: updatedEmployees
      };
    });
  };

  // ── C14 resume rebuild (PRD_StandaloneAdjustments) ──
  // On draft resume, the embedded adjustment copies are refreshed from the LIVE
  // standalone documents — the consumed-by-this-run QUERY is the source of
  // truth, not the saved copies (only the query reveals an entry whose embedded
  // copy was pruned). Live doc wins on amount/description/category and deleted
  // state; entries now outside the employee's tenure or the run's cycle are
  // DROPPED (the next save releases their link via C25 → Pending again) — the
  // CL-001 drifted-day-build pattern applied to adjustments. One-shot per draft.
  const adjustmentRebuildDone = useRef(null);
  const [adjustmentResumeNote, setAdjustmentResumeNote] = useState(null);
  // Settles true when the rebuild finishes (success OR failure) — the C13
  // merge effect below waits on it for resumed drafts (impl-review r2 #2).
  const [adjustmentRebuildSettled, setAdjustmentRebuildSettled] = useState(false);
  useEffect(() => {
    if (!draftRunId || !draftHydrated || !company?.id) return;
    if (adjustmentRebuildDone.current === draftRunId) return;
    adjustmentRebuildDone.current = draftRunId;

    (async () => {
      try {
        const consumed = await fetchConsumedByRun(company.id, draftRunId);
        // Tenure must be judged on the LIVE employee docs (impl-review r2 #1):
        // the hydrated run carries employment as of the LAST SAVE, so an
        // endDate HR shortened mid-draft would never drop the entry here and
        // C5 would then abort every save of this run.
        const employmentById = await fetchEmploymentByIds(
          company.id, consumed.map(c => c.employeeId),
        );
        const ctx = {
          liveById: new Map(consumed.map(c => [c.id, c])),
          excludedIds: new Set(payrollRun.excludedAdjustmentIds || []),
          periodStart: toYMDString(payrollRun.periodStart),
          periodEnd: toYMDString(payrollRun.periodEnd),
          employmentById,
        };

        // Preview against the closure list for the note copy ONLY; the actual
        // write re-derives from `prev` INSIDE the updater (impl-review #2) —
        // the CL-001 refresh above and the Adjustments merge below also write
        // `employees`, and a wholesale write of this pre-await snapshot would
        // silently revert whichever of them landed during the fetch.
        const preview = rebuildRunAdjustments(payrollRun.employees, ctx);
        if (preview.refreshed > 0 || preview.dropped > 0) {
          setPayrollRun(prev => {
            const result = rebuildRunAdjustments(prev.employees, ctx);
            if (result.refreshed === 0 && result.dropped === 0) return prev;
            return { ...prev, employees: result.employees };
          });
          const parts = [];
          if (preview.refreshed > 0) parts.push(`${preview.refreshed} adjustment${preview.refreshed === 1 ? '' : 's'} refreshed from the Adjustments page`);
          if (preview.dropped > 0) parts.push(`${preview.dropped} removed (deleted, excluded, or now outside the period/employment window)`);
          setAdjustmentResumeNote(`Resume refresh: ${parts.join('; ')}.`);
        }
      } catch (err) {
        // Fail-closed: keep the saved copies; the C5 server checks still guard the save.
        console.error('[adjustmentRebuild] resume refresh failed:', err);
        setAdjustmentResumeNote('Couldn\'t re-check this draft\'s adjustments against the Adjustments page — showing the last-saved copies.');
      } finally {
        setAdjustmentRebuildSettled(true); // unblock the C13 merge (r2 #2)
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftRunId, draftHydrated, company?.id]);

  // ── Standalone adjustments in the Adjustments step (PRD_StandaloneAdjustments C11/C13/C19) ──
  // Every creation/edit writes THROUGH adjustmentService to the standalone
  // collection first (C11 — one source of truth; without the write-through, the
  // C14 resume rebuild would revert the runner's edit to the stored copy), then
  // mutates run state. The embedded copy carries sourceId — that is what
  // savePayrollRun claims (C5).

  // Merge pending-in-cycle entries into the run when the Adjustments step opens (C13):
  // pending + dated inside the cycle + inside tenure + employee in the run +
  // not excluded + not already embedded. Entries whose employee is NOT in the
  // run feed the excluded-employee notice instead — without it the loss is
  // invisible at the only moment a human could fix it.
  const [pendingOutsideRun, setPendingOutsideRun] = useState([]);
  useEffect(() => {
    if (currentStep !== 'adjustments' || !company?.id) return;
    if (!payrollRun.periodStart || !payrollRun.periodEnd) return;
    // On a resumed draft the C14 rebuild must land FIRST (impl-review r2 #2):
    // a merged-but-unclaimed entry is not in the consumed set, so a rebuild
    // running on top of the merge would classify it as released and silently
    // drop it from the run. Fresh runs never rebuild (draftHydrated stays
    // false), so they don't wait. The settled flag flips exactly once, which
    // re-fires this effect via its deps.
    if (draftHydrated && !adjustmentRebuildSettled) return;
    let cancelled = false;
    (async () => {
      try {
        const pending = await fetchPendingInCycle(company.id, payrollRun.periodStart, payrollRun.periodEnd);
        if (cancelled) return;
        if (pending.length === 0) {
          setPendingOutsideRun([]);
          return;
        }
        // Tenure from the LIVE employee docs (r2 #1), same source as the
        // rebuild and the C5 server check.
        const employmentById = await fetchEmploymentByIds(
          company.id, pending.map(p => p.employeeId),
        );
        if (cancelled) return;
        // Outside-run notice from the closure state (Step-2 membership is fixed
        // by the time the Adjustments step opens); the embed itself re-derives from `prev`
        // INSIDE the updater — no setState from within an updater (impl-review
        // #9) and no wholesale write of a pre-await snapshot (impl-review #2).
        const preview = mergePendingIntoRun(payrollRun, pending, selectedEmployeeIds, employmentById);
        setPendingOutsideRun(preview.outside);
        setPayrollRun(prev => {
          const result = mergePendingIntoRun(prev, pending, selectedEmployeeIds, employmentById);
          return result.employees ? { ...prev, employees: result.employees } : prev;
        });
      } catch (err) {
        console.error('[ProcessPayroll] pending-adjustments merge failed:', err);
        toast.error('Couldn\'t load pending adjustments for this period — retry by re-opening the Adjustments step.');
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, company?.id, draftHydrated, adjustmentRebuildSettled]);

  // Add manual adjustment (async — writes the standalone doc first, C11)
  const handleAddAdjustment = async (employeeId, adjustment) => {
    const emp = payrollRun.employees.find(e => (e.employeeId || e.id) === employeeId);
    const sourceId = await createAdjustment(
      company.id,
      {
        employeeId,
        type: adjustment.type,
        category: adjustment.category,
        description: adjustment.description,
        amount: adjustment.amount,
        effectiveDate: adjustment.effectiveDate,
      },
      emp?.employment || {},
      user.userData?.userDocId || user.uid,
    );
    const embedded = { ...adjustment, sourceId };
    setPayrollRun(prev => ({
      ...prev,
      employees: prev.employees.map(e => {
        if (e.employeeId === employeeId) {
          const adjustmentType = adjustment.type === 'earning' ? 'earnings' : 'deductions';
          return {
            ...e,
            [adjustmentType]: {
              ...e[adjustmentType],
              adjustments: [...e[adjustmentType].adjustments, embedded]
            }
          };
        }
        return e;
      })
    }));
  };

  // Remove/void manual adjustment (C19 — two distinct acts).
  // 'remove': drop from THIS run only; a sourced entry goes onto the persisted
  //           excludedAdjustmentIds so the resume rebuild doesn't resurrect it,
  //           and the next save releases its link (C25) → Pending again.
  // 'void':   soft-delete the standalone record itself (C18).
  const handleRemoveAdjustment = async (employeeId, adjustmentIndex, adjustmentType, action = 'remove') => {
    const adjustmentCategory = adjustmentType === 'earning' ? 'earnings' : 'deductions';
    const emp = payrollRun.employees.find(e => e.employeeId === employeeId);
    const entry = emp?.[adjustmentCategory]?.adjustments?.[adjustmentIndex];

    if (action === 'void' && entry?.sourceId) {
      // The service re-reads the doc + live index itself (impl-review #4).
      await voidAdjustment(company.id, entry.sourceId, user.userData?.userDocId || user.uid);
    }

    setPayrollRun(prev => ({
      ...prev,
      excludedAdjustmentIds:
        action === 'remove' && entry?.sourceId
          ? [...new Set([...(prev.excludedAdjustmentIds || []), entry.sourceId])]
          : prev.excludedAdjustmentIds,
      employees: prev.employees.map(e => {
        if (e.employeeId === employeeId) {
          const newAdjustments = e[adjustmentCategory].adjustments.filter((_, idx) => idx !== adjustmentIndex);
          return {
            ...e,
            [adjustmentCategory]: {
              ...e[adjustmentCategory],
              adjustments: newAdjustments
            }
          };
        }
        return e;
      })
    }));
  };

  // Edit manual adjustment (async — writes through to the standalone doc, C11/C14).
  // A type flip moves the entry between the earnings/deductions arrays so the
  // embedded placement can never diverge from the stored doc (a divergence
  // aborts the save server-side, C5.3).
  const handleEditAdjustment = async (employeeId, adjustmentIndex, adjustmentType, updatedAdjustment) => {
    if (updatedAdjustment.sourceId) {
      const emp = payrollRun.employees.find(e => e.employeeId === employeeId);
      // The service re-reads the stored doc + live index itself (impl-review #4).
      await updateAdjustment(
        company.id,
        updatedAdjustment.sourceId,
        {
          type: updatedAdjustment.type,
          category: updatedAdjustment.category,
          description: updatedAdjustment.description,
          amount: updatedAdjustment.amount,
          effectiveDate: updatedAdjustment.effectiveDate,
        },
        emp?.employment || {},
        user.userData?.userDocId || user.uid,
      );
    }
    setPayrollRun(prev => ({
      ...prev,
      employees: prev.employees.map(emp => {
        if (emp.employeeId === employeeId) {
          const fromCategory = adjustmentType === 'earning' ? 'earnings' : 'deductions';
          const toCategory = updatedAdjustment.type === 'earning' ? 'earnings' : 'deductions';
          if (fromCategory === toCategory) {
            const newAdjustments = [...emp[fromCategory].adjustments];
            newAdjustments[adjustmentIndex] = updatedAdjustment;
            return {
              ...emp,
              [fromCategory]: { ...emp[fromCategory], adjustments: newAdjustments }
            };
          }
          // Type flipped — move between arrays (placement is what pays).
          return {
            ...emp,
            [fromCategory]: {
              ...emp[fromCategory],
              adjustments: emp[fromCategory].adjustments.filter((_, idx) => idx !== adjustmentIndex)
            },
            [toCategory]: {
              ...emp[toCategory],
              adjustments: [...emp[toCategory].adjustments, updatedAdjustment]
            }
          };
        }
        return emp;
      })
    }));
  };

  // Handle deduction update from DeductionsStep
  // Accepts 4 parameters to match DeductionsStep's callback signature
  // Government deductions (sss, philhealth, pagibig, tax) are stored per-month
  // to support cross-month payroll periods
  const handleDeductionUpdate = (employeeId, monthKey, deductionType, value) => {
    setPayrollRun(prev => ({
      ...prev,
      employees: prev.employees.map(emp => {
        if (emp.employeeId === employeeId) {
          // Government deductions use per-month structure: { '2025-12': 300, '2026-01': 300 }
          const govtDeductions = ['sss', 'philhealth', 'pagibig', 'tax'];

          let updatedDeductions;
          if (govtDeductions.includes(deductionType)) {
            // Per-month structure for government deductions
            const currentDeduction = emp.deductions[deductionType] || {};
            // Ensure it's an object (migrate from old flat number if needed)
            const deductionByMonth = typeof currentDeduction === 'object' && !Array.isArray(currentDeduction)
              ? currentDeduction
              : {};

            updatedDeductions = {
              ...emp.deductions,
              [deductionType]: {
                ...deductionByMonth,
                [monthKey]: value
              }
            };
          } else {
            // Flat structure for other deductions (late, undertime, etc.)
            updatedDeductions = {
              ...emp.deductions,
              [deductionType]: value
            };
          }

          // Helper to sum per-month values
          const sumDeduction = (deduction) => {
            if (typeof deduction === 'object' && !Array.isArray(deduction)) {
              return Object.values(deduction).reduce((sum, val) => sum + (val || 0), 0);
            }
            return deduction || 0;
          };

          // Recalculate total government deductions (SSS, PhilHealth, Pag-IBIG, Tax)
          const totalDeductions = sumDeduction(updatedDeductions.sss) +
                                 sumDeduction(updatedDeductions.philhealth) +
                                 sumDeduction(updatedDeductions.pagibig) +
                                 sumDeduction(updatedDeductions.tax);

          // Recalculate net. Server-compute employees carry no `totals` (it's
          // recomputed client-side, C4) — fall back to the earnings sum so editing a
          // deduction never dereferences undefined.totals.
          const gross = emp.totals?.gross ?? calculateEmployeeFinancials(emp).grossEarnings;
          const net = gross - totalDeductions;

          return {
            ...emp,
            deductions: updatedDeductions,
            totals: {
              ...emp.totals,
              gross,
              totalDeductions,
              net
            }
          };
        }
        return emp;
      })
    }));
  };

  // PRD_LoansManagement C2/C8/C13 — the Loans step lifts every keystroke here, exactly as
  // handleDeductionUpdate does for the government maps, so hasNegativeNet, the Adjustments and Summary
  // columns and summaryTotals read the live array. An entry is one (loanId, monthKey).
  const handleLoanUpdate = (employeeId, entry) => {
    setPayrollRun(prev => ({
      ...prev,
      employees: prev.employees.map(emp => {
        if ((emp.employeeId || emp.id) !== employeeId) return emp;
        const loans = Array.isArray(emp.deductions?.loans) ? emp.deductions.loans : [];
        const i = loans.findIndex(l => l.loanId === entry.loanId && l.monthKey === entry.monthKey);
        const next = i >= 0 ? loans.map((l, j) => (j === i ? entry : l)) : [...loans, entry];
        return { ...emp, deductions: { ...(emp.deductions || {}), loans: next } };
      }),
    }));
  };

  // C13 — the step's mount-time reconcile (drop / clamp / pre-fill) replaces the whole array.
  const handleLoansReplace = (employeeId, entries) => {
    setPayrollRun(prev => ({
      ...prev,
      employees: prev.employees.map(emp =>
        (emp.employeeId || emp.id) === employeeId
          ? { ...emp, deductions: { ...(emp.deductions || {}), loans: entries } }
          : emp
      ),
    }));
  };

  // Save current wizard state as a draft to Firestore
  const doSaveDraft = async ({ silent = false, employeesOverride = null } = {}) => {
    // C1/C2: a never-saved run has no runId yet — it's claimed below at the first save
    // (was `!payrollRun.runId || !company`, which would no-op the reserve-at-save). Still
    // require a period: reserveRunId derives the runId + index boundaries from it, so saving
    // without one would write garbage date boundaries (the recurring date-leak class).
    if (!company || !payrollRun.periodStart || !payrollRun.periodEnd) return false;

    setSavingDraft(true);
    try {
      // Filter employees by selection. `employeesOverride` lets a caller pass a
      // freshly-merged employees array (e.g. handleNext's detail-before-save, C4)
      // that React state hasn't committed yet — without it the save would persist
      // the stale summary-only objects.
      const sourceEmployees = employeesOverride || payrollRun.employees;
      const filteredEmployees = sourceEmployees?.filter(emp =>
        selectedEmployeeIds.has(emp.employeeId || emp.id)
      ) || [];

      const pStart = toYMDString(payrollRun.periodStart);
      const pEnd = toYMDString(payrollRun.periodEnd);

      const draftData = {
        ...payrollRun,
        employees: filteredEmployees,
        selectedEmployeeIds: Array.from(selectedEmployeeIds),
        status: 'draft',
        currentStep, // C9: the step NAME — resolveDraftStep reads it back (a legacy number still resolves)
        lastModified: serverTimestamp(),
        lastModifiedBy: user.userData?.userDocId || user.uid,
        periodStart: pStart,
        periodEnd: pEnd,
      };

      // Add createdAt/createdBy only on first save
      if (!isDraft) {
        draftData.createdAt = serverTimestamp();
        draftData.createdBy = user.userData?.userDocId || user.uid;
      }

      // Denormalize aggregates onto the parent (C2) so collapsed list/approval
      // rows + the dashboard render without fetching the subcollection. Step-4/5
      // edits change net every save, so recompute here (replaces the old
      // `delete draftData.summary`).
      draftData.summary = calculatePayrollSummary(filteredEmployees);

      // C2: claim the runId at the FIRST save (write-if-absent + collision-retry). Thread the
      // returned LOCAL `runId` through the rest of the save — `payrollRun.runId` is still falsy
      // on this tick (setPayrollRun below hasn't committed).
      let runId = payrollRun.runId;
      if (!runId) {
        runId = await reserveRunId({
          startDate: payrollRun.periodStart,
          endDate: payrollRun.periodEnd,
          calendarDays: payrollRun.calendarDays,
        });
      }
      draftData.runId = runId;

      // Persist (C1/C2): the children write goes SERVER-SIDE via the savePayrollRun
      // callable (Admin BulkWriter — the write the client Web SDK can't sustain at scale,
      // OBS-1), then the client writes the parent LAST (children-first/parent-last,
      // PRD_PayrollRunStorage C4).
      await callSavePayrollRun({
        runId,
        employees: filteredEmployees,
        previousEmployeeIds: Array.from(persistedEmployeeIds.current || []),
        mode: 'draft',
      });
      await saveRunParent(company.id, runId, draftData);
      // The subcollection now reflects the selected set — advance the persisted-IDs
      // tracker immediately, decoupled from the non-blocking soft-lock block below
      // (a swallowed lock error must not desync the next save's delete-diff; impl-review #1).
      persistedEmployeeIds.current = new Set(filteredEmployees.map(emp => emp.employeeId || emp.id));

      // Update or add payroll index entry
      const employeeIds = filteredEmployees.map(emp => emp.employeeId || emp.id);
      const indexEntry = {
        runId,
        periodStart: pStart,
        periodEnd: pEnd,
        status: 'draft',
        employeeIds,
      };

      if (isDraft) {
        await updateIndexEntry(company.id, runId, indexEntry);
      } else {
        await addIndexEntry(company.id, indexEntry);
      }

      setIsDraft(true);
      // Commit the claimed runId to state so subsequent saves update (not re-create) the run.
      if (!payrollRun.runId) setPayrollRun(prev => ({ ...prev, runId }));
      setDraftLastSaved(new Date());

      // DTR soft locks are derived from the payroll index (C1): the index entry
      // written above (status 'draft') is what makes this run's records read as
      // soft-locked — no per-employee fan-out.

      if (!silent) {
        toast.success('Draft saved successfully');
      }
      return true;
    } catch (err) {
      console.error('[saveDraft] Error saving draft:', err);
      // R6: the callable failed (or the parent write did) — the client did NOT write the
      // parent on a callable failure, so the run stays in its prior persisted state; resume
      // re-reads the subcollection as the source of truth. Surface + let the user retry.
      toast.error('Failed to save draft. Please try again.');
      return false;
    } finally {
      setSavingDraft(false);
    }
  };

  // QW-1 (OBS-1): single-flight wrapper over doSaveDraft. If a save is already running,
  // a concurrent call awaits the SAME in-flight promise instead of starting a second save
  // (concurrent saves pile mutations into the Firestore write queue → resource-exhausted).
  // Returns the in-flight result so callers that branch on `if (!saved) return` (handleNext)
  // don't treat a coalesced call as a failure.
  const saveDraft = (opts = {}) => {
    if (saveInFlight.current) {
      return saveInFlight.current; // coalesce a concurrent save to the in-flight one
    }
    const p = doSaveDraft(opts).finally(() => { saveInFlight.current = null; });
    saveInFlight.current = p;
    return p;
  };

  // Save & Exit handler — navigate HERE after the save (not inside doSaveDraft), so the
  // exit still happens even if the single-flight guard coalesced this to an in-flight save
  // that didn't carry the exit intent (impl-review #6).
  const handleSaveAndExit = async () => {
    const saved = await saveDraft();
    if (saved) navigate(`/company/${slug}/payroll`);
  };

  // Navigation handlers
  const handleBack = () => {
    const prev = prevStepKey(currentStep); // C1: WIZARD_STEPS drives the order
    if (prev) {
      // BL-018: snapshot the live selection when leaving Step 2 BACKWARD too (handleNext only
      // covers the forward Step-2→3 move). Without this, a Step 2 → Back to Step 1 → forward
      // round trip re-fetches with no snapshot and auto-selects all eligible, silently
      // re-adding deselected employees (test-impl Path A).
      if (currentStep === 'employee-selection') {
        inSessionSelectedIds.current = Array.from(selectedEmployeeIds);
      }
      setCurrentStep(prev);
    }
  };

  // Recalculate gross income from fresh DTR data (staleness resolution)
  const handleRecalculateGrossIncome = async () => {
    if (!company || !payrollRun.periodStart || !payrollRun.periodEnd) return;
    // F3(a): don't recalc while the background staleness check is still resolving
    // (closes the check-vs-recalc overlap on draftLoaded.current).
    if (stalenessChecking) return;
    // F3(b): dedicated re-entrancy guard (a synchronous ref, not the shared `loading`
    // or the `isRecalculating` STATE — state wouldn't update before a second
    // synchronous call). tryEnter() locks synchronously so two rapid calls can't fire
    // concurrent recalcs; recalcGuard.exit() clears it in the finally spanning the
    // full detail-fetch + merge. `isRecalculating` state still drives the button.
    if (!recalcGuard.tryEnter()) return;
    setIsRecalculating(true);

    // Client edits survive the recalc (C5: client wins for the four gov maps, the Loans-step
    // bucket and both adjustment arrays; server wins for earnings/timeRecords and the
    // DTR-derived late/undertime, which re-derive fresh). ONE helper — preserveClientEdits —
    // shared with the resume-refresh rebuild in handleNext, so neither path can drop a
    // bucket the other keeps (PRD_LoansManagement C13: this button used to wipe every
    // loan amount and toast success).
    try {
      setLoading(true);

      // C5(b): detail view for the selected set → REPLACE each line with fresh detail,
      // then re-apply the client-owned buckets OVER the detail.
      {
        const detail = await fetchDetailView(Array.from(selectedEmployeeIds));
        const detailById = new Map(detail.map(d => [d.employeeId || d.id, d]));
        setPayrollRun(prev => ({
          ...prev,
          employees: prev.employees.map(emp => {
            const d = detailById.get(emp.employeeId || emp.id);
            if (!d) return emp; // deselected — leave untouched
            return preserveClientEdits(emp, d);
          }),
        }));
      }

      setStaleEmployees([]);
      setStalenessResolved(true);
      toast.success('Gross income recalculated successfully');
    } catch (err) {
      console.error('[handleRecalculateGrossIncome] Error:', err);
      toast.error('Failed to recalculate. Please try again.');
    } finally {
      setLoading(false);
      setIsRecalculating(false);
      recalcGuard.exit(); // release the synchronous single-flight lock (F3b)
    }
  };

  // Handle Next — advance in client state only (Phase 2D / PRD_PayrollDraftLifecycle C3).
  // No per-Next write of any kind: the early DTR soft-lock is removed (an unsaved draft no
  // longer claims the index), and the heavy children write happens only at Save & Exit /
  // Submit (via the server callable). `advancing` spans the Step-2→3 detail fetch.
  const handleNext = async () => {
    const nextStep = nextStepKey(currentStep); // C1: WIZARD_STEPS drives the order
    if (!nextStep) return;

    // F3(a): the background staleness check is still running — block the same
    // forward transitions the staleEmployees gate covers, rather than advancing
    // past Step 3 on gross income that may be stale (no await/deadlock — just retry).
    if (blocksForwardCommit(stalenessChecking, nextStep)) {
      toast.warning('Checking time records… please wait a moment and try again.');
      return;
    }

    // CL-001 (C10): the resume freshness refresh is still in flight — it can flip an
    // employee's incomplete flag or drop their day-build, so hold EVERY forward move
    // (including Step 2→3, which blocksForwardCommit deliberately doesn't cover).
    if (blocksWhileResumeRefreshing(resumeRefreshing, nextStep)) {
      toast.warning('Refreshing the saved draft… please wait a moment and try again.');
      return;
    }

    // Block advancement past Step 3 if staleness detected and not yet resolved (the
    // target list lives in payrollGuards.js beside its four siblings — C1).
    if (blocksAdvanceOnUnresolvedStaleness(staleEmployees.length, stalenessResolved, nextStep)) {
      toast.warning('Please recalculate gross income before proceeding. Time records have changed since this draft was saved.');
      return;
    }

    // Advancing acknowledges the amber resume warning (employees removed on resume) — clear it
    // on any forward move. A failed-refresh warning never reaches here: the Next button is
    // disabled while resumeRefreshing is set.
    setResumeRefreshWarning(null);

    // C3: no per-Next write — advance in client state; the heavy save happens only at
    // Save & Exit / Submit. `advancing` spans the Step-2→3 detail fetch, locking Step-2 controls.
    setAdvancing(true);
    try {
      if (currentStep === 'employee-selection') {
        // CL-001: advancing past Step 2 IS the rebuild point (the detail refetch below), so the
        // "will rebuild on continue" banner has served its purpose — clear it (and don't let it
        // reappear on a later Back to Step 2, since the build is now fresh).
        setResumeRebuildPending(false);
        // C4: the baseline must read the DETAIL-loaded employees — default to current
        // state and override with the freshly-merged local when a detail fetch runs.
        let baselineSource = payrollRun.employees || [];

        // C4/R2: fetch the full detail (raw timeRecords + earnings/deductions/monthlyDtrData)
        // for the selected employees that LACK it and merge ADDITIVELY. Re-gated on MISSING
        // `timeRecords` (NOT `draftLoaded.current`, which permanently disabled the fetch on a
        // resumed run — dead-ending a created-never-saved run at Step 3 with zero gross, R2).
        const needDetail = (payrollRun.employees || [])
          .filter(emp => selectedEmployeeIds.has(emp.employeeId || emp.id))
          .filter(emp => !emp.timeRecords || emp.timeRecords.length === 0)
          .map(emp => emp.employeeId || emp.id);
        if (needDetail.length > 0) {
          try {
            const detail = await fetchDetailView(needDetail);
            const detailById = new Map(detail.map(d => [d.employeeId || d.id, d]));
            // C4: HOIST the merge to a local — reading payrollRun.employees after the async
            // setPayrollRun would see pre-merge summary-only lines (empty baseline, the OBS-4
            // shape). Feed the same `merged` array to state AND the baseline.
            const merged = (payrollRun.employees || []).map(emp => {
              const empId = emp.employeeId || emp.id;
              const d = detailById.get(empId);
              // additive: only fill summary-only lines; never clobber an edited detail line.
              // A resumed line (rebuilt from the live summary, no timeRecords) still carries the
              // runner's edits — re-apply them over the fresh detail (PRD_LoansManagement C13:
              // this path used to lose every deduction, loan and adjustment on that line).
              return (d && (!emp.timeRecords || emp.timeRecords.length === 0)) ? preserveClientEdits(emp, { ...emp, ...d }) : emp;
            });
            setPayrollRun(prev => ({ ...prev, employees: merged }));
            baselineSource = merged;
          } catch (err) {
            console.error('[handleNext] Detail fetch failed:', err);
            toast.error('Failed to load time records. Please try again.');
            return; // outer finally clears `advancing`
          }
        }

        // C4: capture the in-memory DTR baseline + (re-)arm the Summary check — fresh in-session
        // runs only (a resumed run is rebuilt on resume and has no Summary check, PRD_PayrollResumeReprice
        // C2). Reset prior staleness so the new baseline starts clean.
        if (inSessionRunRef.current) {
          const pStart = toYMDString(payrollRun.periodStart);
          const pEnd = toYMDString(payrollRun.periodEnd);
          const selected = baselineSource.filter(emp => selectedEmployeeIds.has(emp.employeeId || emp.id));
          dtrBaselineRef.current = { snapshot: buildDtrSnapshot(selected, pStart, pEnd), periodStart: pStart, periodEnd: pEnd };
          summaryCheckArmed.current = true;
          setStaleEmployees([]);
          setStalenessResolved(false);
        }

        // BL-018: snapshot the live selection so a Step-2 re-mount on back-nav restores it
        // (fed as initialSelectedIds) instead of auto-selecting all eligible again.
        inSessionSelectedIds.current = Array.from(selectedEmployeeIds);
      }

      setCurrentStep(nextStep);
    } finally {
      setAdvancing(false);
    }
  };

  // BL-022(1): while resuming a draft, hold the spinner until loadDraft sets the real step —
  // otherwise Step 1 (Period Selection) flashes briefly before the async load jumps to the
  // saved step. (draftResuming initializes to !!draftRunId; a hydrated draft holds it
  // through the CL-001 resume-refresh effect — so a stale Summary never paints
  // before the freshness clamp — and every other path clears it in loadDraft's finally.)
  if (companyLoading || draftResuming) {
    return <div className="loading-spinner">Loading...</div>;
  }

  if (!company) {
    return <div className="error-message">Company not found</div>;
  }

  // Lock a step's inputs while the run is busy: saving, advancing, OR running the
  // in-session staleness check on landing at Summary (parallelized to ~2s, so the lock is
  // brief — the "Checking time records…" indicator explains it). A native
  // <fieldset disabled> disables every native control inside; the opacity +
  // pointer-events also grey out and neutralize custom (non-form-control) elements
  // like the Quick-Template cards. (Once the check resolves, fields are editable
  // again — including while the stale banner is shown, so deductions can be edited
  // before recalculating.)
  const stepLocked = savingDraft || advancing || stalenessChecking;
  const fieldsetReset = {
    border: 0, margin: 0, padding: 0, minInlineSize: 0,
    opacity: stepLocked ? 0.55 : 1,
    pointerEvents: stepLocked ? 'none' : 'auto',
    transition: 'opacity 0.15s ease',
  };

  return (
    <div className="payroll-process-container">
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '12px',
        flexWrap: 'wrap',
        margin: '0 0 0.75rem 0',
      }}>
        <h1 style={{ fontSize: "1.25rem", fontWeight: "700", color: "var(--ui-primary)", margin: 0 }}>
          Process Payroll
        </h1>

        {/* Draft status — right-aligned on the title row (compact; no full-width bar) */}
        {payrollRun.runId && currentStep !== 'period-selection' && isDraft && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
            <span style={{
              padding: '1px 6px',
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '10px',
              textTransform: 'uppercase',
              letterSpacing: '0.3px',
              background: 'var(--ui-success-tint)',
              color: 'var(--ui-success-text)',
            }}>
              Draft
            </span>
            <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 600, color: 'var(--ui-text)', fontSize: '11px' }}>
              {payrollRun.runId}
            </span>
            <span style={{ color: 'var(--ui-border)' }}>&bull;</span>
            <span style={{ color: 'var(--ui-text-muted)' }}>
              Last saved: {draftLastSaved
                ? (() => {
                    const diffMs = Date.now() - draftLastSaved.getTime();
                    if (diffMs < 60000) return 'just now';
                    return draftLastSaved.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                      + ' ' + draftLastSaved.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                  })()
                : 'unknown'}
            </span>
          </div>
        )}
      </div>

      {/* Progress Stepper with Blue-to-Gold Gradient (7 steps — WIZARD_STEPS, PRD_LoansManagement
          C1). The inline width is the whole rule: no `--step-N` CSS rule exists (employees.css). */}
      <div className="stepper-container">
        {/* Progress Bar */}
        <div className="progress-bar">
          <div
            className={`progress-bar-fill progress-bar-fill--step-${stepNumber(currentStep)}`}
            style={{ width: `${stepProgressWidth(currentStep)}%` }}
          ></div>
        </div>

        <div className="stepper">
          {WIZARD_STEPS.map((step, i) => (
            <div
              key={step.key}
              className={`stepper-step ${currentStep === step.key ? 'stepper-step--active' : ''} ${
                WIZARD_STEP_KEYS.indexOf(currentStep) > i ? 'stepper-step--completed' : ''
              }`}
            >
              <div className="stepper-step__indicator">{step.number}</div>
              <div className="stepper-step__label">{step.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Draft status moved to the title row above */}

      {/* Checking indicator — brief, while the in-session staleness recount runs on
          landing at Summary (parallelized). Explains why Next is momentarily disabled. */}
      {stalenessChecking && (
        <div style={{
          margin: '0 0 16px 0',
          padding: '12px 16px',
          background: 'var(--ui-primary-tint)',
          border: '1px solid var(--ui-primary)',
          borderRadius: '10px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          fontSize: '0.875rem',
          color: 'var(--ui-primary)',
          fontWeight: 600,
        }}>
          <span className="es-spinner" style={{ width: 16, height: 16 }} />
          Checking time records for changes…
        </div>
      )}

      {adjustmentResumeNote && (
        <div style={{
          margin: '0 0 16px 0',
          padding: '12px 16px',
          background: 'var(--ui-primary-tint)',
          border: '1px solid var(--ui-primary)',
          borderRadius: '10px',
          color: 'var(--ui-primary-hover)',
          fontSize: 13,
          lineHeight: 1.5,
        }}>
          <Icon name="info" size={14} aria-hidden="true" /> {adjustmentResumeNote}
        </div>
      )}
      {/* PRD_PayrollResumeReprice C2/C6: amber resume warning (not a transient toast) — either
          employees were removed from the run as no longer eligible, or the summary call
          failed and the run is held (Next/Submit disabled) until a reload. Non-dismissable;
          handleNext clears the removal notice on the next forward move. */}
      {resumeRefreshWarning && (
        <div style={{
          margin: '0 0 16px 0',
          padding: '12px 16px',
          background: 'var(--ui-warning-tint)',
          border: '1px solid var(--ui-warning)',
          borderRadius: '10px',
          color: 'var(--ui-warning-text)',
          fontSize: 13,
          lineHeight: 1.5,
        }}>
<Icon name="alert-triangle" size={14} aria-hidden="true" /> {resumeRefreshWarning}
        </div>
      )}

      {/* Staleness Banner — shown when the in-session Summary check finds DTR data changed
          since the Step 2→3 baseline */}
      {staleEmployees.length > 0 && !stalenessResolved && (
        <div style={{
          margin: '0 0 16px 0',
          padding: '14px 18px',
          background: 'var(--ui-warning-tint)',
          border: '1px solid var(--ui-warning)',
          borderRadius: '10px',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '10px',
            fontWeight: 600,
            fontSize: '0.875rem',
            color: 'var(--ui-warning-text)',
          }}>
            <Icon name="alert-triangle" size={18} />
            Time records modified since last save
          </div>
          <ul style={{ margin: '0 0 12px 0', padding: '0 0 0 24px', fontSize: '0.8125rem', color: 'var(--ui-warning-text)', lineHeight: '1.6' }}>
            {staleEmployees.map(emp => (
              <li key={emp.employeeId}>
                <strong>{emp.employeeName}</strong> ({emp.employeeId}) — {emp.changes.join(', ')}
              </li>
            ))}
          </ul>
          <button
            onClick={handleRecalculateGrossIncome}
            disabled={isRecalculating || stalenessChecking}
            style={{
              padding: '8px 16px',
              background: 'var(--ui-warning)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '0.8125rem',
              fontWeight: 600,
              cursor: (isRecalculating || stalenessChecking) ? 'not-allowed' : 'pointer',
              opacity: (isRecalculating || stalenessChecking) ? 0.6 : 1,
            }}
          >
            {isRecalculating ? 'Recalculating…' : 'Recalculate Gross Income'}
          </button>
        </div>
      )}

      <div className="payroll-content">
        {currentStep === 'period-selection' && (
          <PeriodSelection
            onPeriodConfirm={handlePeriodChange}
            companySettings={companySettings}
            companyId={company?.id}
            initialStartDate={payrollRun.periodStart}
            initialEndDate={payrollRun.periodEnd}
            cutoffStartDays={cutoffStartDays}
            lastCompletedPeriodEnd={lastCompletedPeriodEnd}
            readOnly={isDraft || hasEnteredStep2}
          />
        )}

        {currentStep === 'employee-selection' && piiExcludedCount > 0 && (
          <div
            style={{
              padding: '12px 16px',
              marginBottom: '16px',
              backgroundColor: 'var(--ui-accent-tint)',
              border: '1px solid var(--ui-accent)',
              borderRadius: 8,
              color: 'var(--ui-warning-text)',
              fontSize: 13,
              lineHeight: 1.5,
            }}
          >
            <strong>{piiExcludedCount}</strong> {piiExcludedCount === 1 ? 'employee' : 'employees'} excluded
            {' '}— PII incomplete (banking, salary, or government ID missing). Update via Manage Employee and requires Sensitive Information access in Roles settings.
          </div>
        )}

        {currentStep === 'summary' && hasNegativeNet && (
          <div
            style={{
              padding: '12px 16px',
              marginBottom: '16px',
              backgroundColor: 'var(--ui-danger-tint)',
              border: '1px solid var(--ui-danger)',
              borderRadius: 8,
              color: 'var(--ui-danger-text)',
              fontSize: 13,
              lineHeight: 1.5,
            }}
          >
            <strong>{negativeNetEmployees.length}</strong> {negativeNetEmployees.length === 1 ? 'employee has' : 'employees have'} a <strong>negative net pay</strong> — their deductions, loans and adjustments exceed what they earned this period (the employee owes the company). Submitting is blocked because a payroll run cannot disburse a negative amount to the bank file. Adjust their deductions, loans or adjustments so net pay is at least ₱0.
            {' '}Affected: {negativeNetEmployees.map(emp => emp.personalInfo?.fullName || emp.employeeName || emp.employeeId).join(', ')}.
          </div>
        )}

        {/* PRD_PayrollResumeReprice C2: persistent blue banner after a resume rebuilt every line. Explains
            the bounce-back to Step 2 and the pending pay rebuild (a transient toast vanished
            before it could be read — test-impl S3). Suppressed when the yellow incomplete
            or split-day banner is up (that must be resolved first; the rebuild is moot until then). */}
        {currentStep === 'employee-selection' && resumeRebuildPending && !hasIncompleteSelected && !hasSplitDaySelected && (
          <div
            style={{
              padding: '12px 16px',
              marginBottom: '16px',
              backgroundColor: 'var(--ui-primary-tint)',
              border: '1px solid var(--ui-primary)',
              borderRadius: 8,
              color: 'var(--ui-primary-hover)',
              fontSize: 13,
              lineHeight: 1.5,
            }}
          >
<Icon name="info" size={14} aria-hidden="true" /> Pay will be re-calculated with current rates and time records when you continue to Gross Income.
          </div>
        )}

        {/* CL-001 (C10): also rendered on Summary — a Summary resume whose refresh set the
            flag has no Step-2 banner on screen, yet Submit is disabled on it. */}
        {(currentStep === 'employee-selection' || currentStep === 'summary') && hasIncompleteSelected && (
          <div
            style={{
              padding: '12px 16px',
              marginBottom: '16px',
              backgroundColor: 'var(--ui-warning-tint)',
              border: '1px solid var(--ui-warning)',
              borderRadius: 8,
              color: 'var(--ui-warning-text)',
              fontSize: 13,
              lineHeight: 1.5,
            }}
          >
<Icon name="alert-triangle" size={14} aria-hidden="true" /> Incomplete time {incompleteSelectedEmployees.length === 1 ? 'record' : 'records'} — complete {incompleteSelectedEmployees.length === 1 ? 'it' : 'them'} or de-select to continue.
          </div>
        )}

        {/* PRD_ShiftRules C5: the same placement and style as the incomplete banner. */}
        {(currentStep === 'employee-selection' || currentStep === 'summary') && hasSplitDaySelected && (
          <div
            style={{
              padding: '12px 16px',
              marginBottom: '16px',
              backgroundColor: 'var(--ui-warning-tint)',
              border: '1px solid var(--ui-warning)',
              borderRadius: 8,
              color: 'var(--ui-warning-text)',
              fontSize: 13,
              lineHeight: 1.5,
            }}
          >
<Icon name="alert-triangle" size={14} aria-hidden="true" /> More than one entry on a day — keep one entry per day in Employee Time Records, or de-select to continue.
          </div>
        )}

        {currentStep === 'employee-selection' && (
          <EmployeePayrollList
            employees={payrollRun.employees}
            periodStart={payrollRun.periodStart}
            periodEnd={payrollRun.periodEnd}
            onEmployeeUpdate={handleEmployeeUpdate}
            onSelectionChange={setSelectedEmployeeIds}
            companySettings={companySettings}
            payrollIndexRuns={payrollIndexRuns}
            currentRunId={draftRunId || payrollRun.runId}
            initialSelectedIds={inSessionSelectedIds.current || draftInitialSelectedIds.current}
            employeesLoading={loading}
            employeesError={employeesError}
            onRetry={() =>
              fetchEmployeesForPayroll(payrollRun.periodStart, payrollRun.periodEnd, company?.id)
            }
            busy={advancing}
          />
        )}

        {currentStep === 'gross-income' && (
          <fieldset disabled={stepLocked} style={fieldsetReset}>
            <GrossIncomeBreakdown
              employees={payrollRun.employees.filter(emp =>
                selectedEmployeeIds.has(emp.employeeId || emp.id)
              )}
              periodStart={payrollRun.periodStart}
              periodEnd={payrollRun.periodEnd}
              companySettings={companySettings}
              payrollRates={payrollRates}
              attendancePolicy={attendancePolicy}
              computedRates={computedRates}
            />
          </fieldset>
        )}

        {currentStep === 'deductions' && (
          <fieldset disabled={stepLocked} style={fieldsetReset}>
            <DeductionsStep
              employees={payrollRun.employees.filter(emp =>
                selectedEmployeeIds.has(emp.employeeId || emp.id)
              )}
              periodStart={payrollRun.periodStart}
              periodEnd={payrollRun.periodEnd}
              onDeductionUpdate={handleDeductionUpdate}
            />
          </fieldset>
        )}

        {/* PRD_LoansManagement C1: the Loans step sits between Deductions and Adjustments.
            Amounts lift live into payrollRun.employees[].deductions.loans (C8/C15). */}
        {currentStep === 'loans' && (
          <fieldset disabled={stepLocked} style={fieldsetReset}>
            <LoansStep
              employees={payrollRun.employees.filter(emp =>
                selectedEmployeeIds.has(emp.employeeId || emp.id)
              )}
              periodStart={payrollRun.periodStart}
              periodEnd={payrollRun.periodEnd}
              companyId={company?.id}
              onLoanUpdate={handleLoanUpdate}
              onLoansReplace={handleLoansReplace}
              onBlockedChange={setLoansStepBlocked}
            />
          </fieldset>
        )}

        {currentStep === 'adjustments' && (
          <fieldset disabled={stepLocked} style={fieldsetReset}>
            <ManualAdjustments
              employees={payrollRun.employees.filter(emp =>
                selectedEmployeeIds.has(emp.employeeId || emp.id)
              )}
              periodStart={payrollRun.periodStart}
              periodEnd={payrollRun.periodEnd}
              onAddAdjustment={handleAddAdjustment}
              onEditAdjustment={handleEditAdjustment}
              onRemoveAdjustment={handleRemoveAdjustment}
              pendingOutsideRun={pendingOutsideRun}
            />
          </fieldset>
        )}

        {currentStep === 'summary' && payrollRunForSummary && (
          <PayrollSummary
            payrollRun={payrollRunForSummary}
            company={company}
            onTotalsCalculated={setSummaryTotals}
          />
        )}

      </div>

      <div className="payroll-navigation">
        {/* Back Button - Always visible except on first step */}
        {currentStep !== 'period-selection' && (
          <button
            className="btn-secondary"
            onClick={handleBack}
            disabled={loading || advancing}
          >
            <Icon name="arrow-left" size={14} aria-hidden="true" /> Back
          </button>
        )}

        <div className="nav-right-buttons">
          {/* UX-1: clear progress message while the Step-2→3 detail fetch + save runs */}
          {advancing && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              marginRight: '0.75rem', fontSize: '0.8125rem', color: 'var(--ui-primary)', fontWeight: 600,
            }}>
              <span className="es-spinner" style={{ width: 14, height: 14 }} />
              {currentStep === 'employee-selection' ? 'Preparing gross income…' : 'Saving…'}
            </span>
          )}
          {/* Save & Exit - visible once past Step 1 (a fresh in-session run has no runId yet —
              it's claimed at this first save; a resumed draft has isDraft). Gating on runId
              would hide the button for the entire life of a never-saved run (impl-review blocker). */}
          {(isDraft || hasEnteredStep2) && currentStep !== 'period-selection' && (
            <button
              className="btn-secondary"
              onClick={handleSaveAndExit}
              disabled={loading || advancing || savingDraft || (currentStep === 'employee-selection' && selectedEmployeeIds.size === 0) || (currentStep === 'employee-selection' && Boolean(employeesError))}
              style={{
                marginRight: '0.5rem',
                border: '1px solid var(--ui-border)',
              }}
            >
              {savingDraft ? 'Saving...' : 'Save & Exit'}
            </button>
          )}

          {/* Next Button - Changes to "Complete" on summary step */}
          {currentStep === 'period-selection' ? (
            // No Next button on period selection - handled by period component
            null
          ) : currentStep === 'summary' ? (
            <button
              className="btn-success"
              onClick={handleProcessPayroll}
              disabled={loading || stalenessChecking || resumeRefreshing || (staleEmployees.length > 0 && !stalenessResolved) || hasNegativeNet || hasIncompleteSelected || hasSplitDaySelected}
              title={hasNegativeNet
                ? `${negativeNetEmployees.length} employee${negativeNetEmployees.length === 1 ? ' has' : 's have'} a negative net pay. Adjust deductions, loans or adjustments so net pay is at least ₱0 before submitting.`
                : hasIncompleteSelected
                  ? `Incomplete time records (missing a time-in or time-out) for: ${incompleteSelectedEmployees.map(e => e.employeeId || e.id).join(', ')}. Fix them in Employee Time Records (filter: Incomplete), or de-select the employee to pay them next cycle.`
                  : hasSplitDaySelected
                    ? splitDayReasonText(splitDaySelectedEmployees)
                    : undefined}
            >
              {loading ? 'Submitting...' : 'Submit for Approval'}
            </button>
          ) : (
            // Negative-net gate is the FINALIZE boundary only (adjustments → summary), NOT both
            // Deductions and Adjustments. A negative net can originate on either step (a gov
            // deduction OR a manual adjustment), so blocking Next on Deductions — or Back on
            // Adjustments — would strand the user away from the step holding the cause
            // (test-impl Obs 3). Deductions↔Adjustments stay freely navigable; Summary + Submit
            // remain blocked (Submit button + handleSubmitConfirm backstop) so nothing finalizes negative.
            <button
              className="btn-primary"
              onClick={handleNext}
              disabled={loading || advancing || savingDraft || resumeRefreshing || (currentStep === 'employee-selection' && Boolean(employeesError)) || (currentStep === 'employee-selection' && selectedEmployeeIds.size === 0) || (currentStep === 'employee-selection' && hasUnresolvedOT) || (currentStep === 'employee-selection' && hasIncompleteSelected) || (currentStep === 'employee-selection' && hasSplitDaySelected) || (currentStep === 'adjustments' && hasNegativeNet) || (currentStep === 'loans' && loansStepBlocked) || disablesNextOnUnresolvedStaleness(staleEmployees.length, stalenessResolved, currentStep) || disablesNextWhileChecking(stalenessChecking, currentStep)}
              title={
                currentStep === 'employee-selection' && hasIncompleteSelected
                  ? `Incomplete time records (missing a time-in or time-out) for: ${incompleteSelectedEmployees.map(e => e.employeeId || e.id).join(', ')}. Fix them in Employee Time Records (filter: Incomplete), or de-select the employee to pay them next cycle.`
                  : currentStep === 'employee-selection' && hasSplitDaySelected
                    ? splitDayReasonText(splitDaySelectedEmployees)
                  : currentStep === 'employee-selection' && hasUnresolvedOT
                    ? 'Resolve pending/rejected OT approvals before proceeding'
                    : (currentStep === 'adjustments' && hasNegativeNet)
                      ? `${negativeNetEmployees.length} employee${negativeNetEmployees.length === 1 ? ' has' : 's have'} a negative net pay. Reduce their deductions, loans or adjustments so net pay is at least ₱0.`
                      : undefined
              }
            >
              {advancing ? 'Loading…' : 'Next →'}
            </button>
          )}
        </div>
      </div>

      {/* Complete Payroll Modal */}
      <CompletePayrollModal
        isOpen={showCompleteModal}
        onClose={() => setShowCompleteModal(false)}
        onConfirm={handleSubmitConfirm}
        payrollRun={payrollRun}
        summaryTotals={summaryTotals}
        companyId={company?.id}
        company={company}
      />
    </div>
  );
};

export default ProcessPayroll;